/* eslint-disable quotes, quote-props, comma-dangle, max-len, indent, no-multiple-empty-lines */
/**
 * Disease → drug target seed data for Gibbs clarification cascade.
 *
 * This file is partially machine-generated — see
 * docs/gibbs_abstract_goal_decomposition_spec.md for the design.
 *
 * Sources:
 *   - Host-target diseases (20): Open Targets Platform GraphQL API,
 *     cross-referenced to UniProt for PDB structure counts.
 *   - Hepatitis B: hand-crafted from UniProt HBV proteome queries
 *     (taxonomy_id 10407) with maturity annotations drawn from
 *     published drug-discovery literature. NEEDS MEDCHEM SME REVIEW.
 *   - Other infectious diseases (hep C, HIV, COVID-19, TB): stubs
 *     awaiting the same treatment as hep B.
 *
 * To regenerate: see seed scripts in /tmp/seed_disease_targets.py
 * and /tmp/compose_disease_targets.py from the session that created
 * this file, or re-query the APIs directly.
 *
 * DO NOT hand-edit the host-target entries unless you also update
 * the _meta.lastUpdated field and note the manual override.
 * Infectious-disease entries ARE meant to be hand-edited by SMEs.
 */

export const diseaseTargets = {
    "_meta": {
        "lastUpdated": "2026-04-28",
        "diseaseCount": 55,
        "sources": {
            "openTargets": {
                "endpoint": "https://api.platform.opentargets.org/api/v4/graphql",
                "usedFor": "20 host-target diseases",
                "queriedAt": "2026-04-14"
            },
            "uniprot": {
                "endpoint": "https://rest.uniprot.org/uniprotkb",
                "usedFor": "PDB structure counts for all targets, viral proteome for hep B",
                "queriedAt": "2026-04-14"
            }
        },
        "reviewStatus": {
            "host_targets": "draft \u2014 Open Targets association scores are reliable, but medchem judgment on tractability / IP / clinical precedent is not encoded",
            "infectious_diseases": "draft \u2014 hep B hand-crafted from UniProt, needs medchem SME review on maturity annotations. hep C / HIV / COVID / TB are stubs."
        }
    },
    "diseases": {
        "hepatitis_b": {
            "canonicalName": "Hepatitis B virus infection",
            "aliases": [
                "hep B",
                "hepB",
                "hep-B",
                "HBV",
                "hepatitis B",
                "hepatitis B virus",
                "chronic hepatitis B"
            ],
            "efoId": "EFO_0004197",
            "targetSource": "uniprot + hand-curated maturity (needs SME review)",
            "topTargets": [
                {
                    "geneSymbol": "C",
                    "name": "HBV capsid protein / core antigen (HBcAg)",
                    "uniprot": "P03146",
                    "organism": "Hepatitis B virus",
                    "pdbCount": 25,
                    "preferredDemoPdb": "5E0I",
                    "preferredDemoPdbNote": "HBV capsid Y132A mutant in complex with NVR10-001E2, a capsid assembly modulator. Co-crystal ligand gives BMaps an auto-detected binding site for downstream design-cycle workflows.",
                    "pdbIds": [
                        "5E0I",
                        "6HTX",
                        "6HU4",
                        "6HU7",
                        "7OCO",
                        "7OCW"
                    ],
                    "maturity": "unreviewed",
                    "maturityNote": "DRAFT — pending medchem SME review. Initial draft: Capsid assembly modulators (CAMs) are an active clinical space. Several candidates (vebicorvir, JNJ-6379 class) have reached Phase II. Structure-based design is well-supported.",
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "P",
                    "name": "HBV polymerase / reverse transcriptase",
                    "uniprot": "P03156",
                    "organism": "Hepatitis B virus",
                    "pdbCount": 0,
                    "pdbIds": [],
                    "maturity": "unreviewed",
                    "maturityNote": "DRAFT — pending medchem SME review. Initial draft: NRTIs (entecavir, tenofovir, lamivudine) approved and dominate the space. IP crowded. No experimental apo structure — structure-based design requires AlphaFold model or homology from HIV RT.",
                    "structureSource": "alphafold",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "SLC10A1",
                    "name": "NTCP sodium/bile acid cotransporter (host entry receptor)",
                    "uniprot": "Q14973",
                    "organism": "Homo sapiens",
                    "pdbCount": 10,
                    "pdbIds": [
                        "7FCI",
                        "7PQG",
                        "7PQQ",
                        "7VAD",
                        "7VAG"
                    ],
                    "maturity": "unreviewed",
                    "maturityNote": "DRAFT — pending medchem SME review. Initial draft: Myrcludex B / bulevirtide is an EMA-approved peptide entry inhibitor (for HDV coinfection). Small-molecule NTCP inhibitors are exploratory. Recent cryo-EM structures (2021+) enable SBDD.",
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "S",
                    "name": "HBV large envelope protein / HBsAg",
                    "uniprot": "P03138",
                    "organism": "Hepatitis B virus",
                    "pdbCount": 0,
                    "pdbIds": [],
                    "maturity": "unreviewed",
                    "maturityNote": "DRAFT — pending medchem SME review. Initial draft: Functional cure endpoint is HBsAg loss. siRNAs and antisense (bepirovirsen, imdusiran) target HBsAg at the mRNA level. Small-molecule binding pocket is not well-characterized. No experimental structure.",
                    "structureSource": "none",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "X",
                    "name": "HBV protein X (HBx)",
                    "uniprot": "P03165",
                    "organism": "Hepatitis B virus",
                    "pdbCount": 0,
                    "pdbIds": [],
                    "maturity": "unreviewed",
                    "maturityNote": "DRAFT — pending medchem SME review. Initial draft: Regulatory viral protein, partially disordered. No approved drugs. No experimental structure. Implicated in HCC development but druggability unclear.",
                    "structureSource": "none",
                    "reviewRequired": true
                }
            ]
        },
        "hepatitis_c": {
            "canonicalName": "Hepatitis C virus infection",
            "aliases": ["hep C", "HCV", "hepatitis C", "hepatitis C virus"],
            "targetSource": "uniprot + known drug targets (needs SME review)",
            "topTargets": [
                {
                    "geneSymbol": "NS3/4A",
                    "name": "Serine protease NS3/NS4A",
                    "uniprot": "P26664",
                    "organism": "HCV",
                    "pdbCount": 34,
                    "preferredDemoPdb": "1RTL",
                    "pdbIds": ["1RTL", "2A4G", "2GVF", "3EYD", "3HKW", "3KN2"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "NS5B",
                    "name": "RNA-directed RNA polymerase NS5B",
                    "uniprot": "P26663",
                    "organism": "HCV",
                    "pdbCount": 101,
                    "preferredDemoPdb": "1A1Q",
                    "pdbIds": ["1A1Q", "1BT7", "1C2P", "1CSJ", "1CU1", "1GX5"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "NS5A",
                    "name": "Non-structural protein 5A",
                    "uniprot": "P26662",
                    "organism": "HCV",
                    "pdbCount": 16,
                    "preferredDemoPdb": "1DXP",
                    "pdbIds": ["1DXP", "1DY8", "1DY9", "1W3C", "2K8J", "3OYP"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "E2",
                    "name": "Envelope glycoprotein E2",
                    "uniprot": "P26661",
                    "organism": "HCV",
                    "pdbCount": 5,
                    "preferredDemoPdb": "1XCQ",
                    "pdbIds": ["1XCQ", "1XCT", "1XF5", "3GSZ", "3HVO"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "NS4B",
                    "name": "Non-structural protein 4B",
                    "uniprot": "O39929",
                    "organism": "HCV",
                    "pdbCount": 2,
                    "preferredDemoPdb": "6P6T",
                    "pdbIds": ["6P6T", "6P6Z"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                }
            ]
        },
        "hiv": {
            "canonicalName": "HIV infection / AIDS",
            "aliases": ["HIV", "AIDS", "HIV-1", "human immunodeficiency virus"],
            "targetSource": "uniprot + known drug targets (needs SME review)",
            "topTargets": [
                {
                    "geneSymbol": "PR",
                    "name": "Protease",
                    "uniprot": "P03366",
                    "organism": "HIV-1",
                    "pdbCount": 446,
                    "preferredDemoPdb": "1A9M",
                    "pdbIds": ["1A9M", "1AJV", "1AJX", "1AXA", "1BQM", "1BQN"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "RT",
                    "name": "Reverse transcriptase/RNaseH p66",
                    "uniprot": "P03366",
                    "organism": "HIV-1",
                    "pdbCount": 446,
                    "preferredDemoPdb": "1A9M",
                    "pdbIds": ["1A9M", "1AJV", "1AJX", "1AXA", "1BQM", "1BQN"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "IN",
                    "name": "Integrase",
                    "uniprot": "P03366",
                    "organism": "HIV-1",
                    "pdbCount": 446,
                    "preferredDemoPdb": "1A9M",
                    "pdbIds": ["1A9M", "1AJV", "1AJX", "1AXA", "1BQM", "1BQN"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "gp120",
                    "name": "Envelope glycoprotein gp160 (gp120 subunit)",
                    "uniprot": "P04578",
                    "organism": "HIV-1",
                    "pdbCount": 131,
                    "preferredDemoPdb": "1AIK",
                    "pdbIds": ["1AIK", "1DF4", "1DF5", "1DLB", "1FAV", "1G9M"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "CA",
                    "name": "Capsid protein p24",
                    "uniprot": "P03348",
                    "organism": "HIV-1",
                    "pdbCount": 0,
                    "pdbIds": [],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "alphafold",
                    "reviewRequired": true
                }
            ]
        },
        "covid_19": {
            "canonicalName": "COVID-19",
            "aliases": ["COVID", "SARS-CoV-2", "coronavirus disease", "COVID-19"],
            "targetSource": "uniprot + known drug targets (needs SME review)",
            "topTargets": [
                {
                    "geneSymbol": "3CLpro",
                    "name": "Main protease (3C-like protease, Mpro)",
                    "uniprot": "P0DTD1",
                    "organism": "SARS-CoV-2",
                    "pdbCount": 2422,
                    "preferredDemoPdb": "5R7Y",
                    "pdbIds": ["5R7Y", "5R7Z", "5R80", "5R81", "5R82", "5R83"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "RdRp",
                    "name": "RNA-dependent RNA polymerase (nsp12)",
                    "uniprot": "P0DTD1",
                    "organism": "SARS-CoV-2",
                    "pdbCount": 2422,
                    "preferredDemoPdb": "5R7Y",
                    "pdbIds": ["5R7Y", "5R7Z", "5R80", "5R81", "5R82", "5R83"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "PLpro",
                    "name": "Papain-like protease (nsp3)",
                    "uniprot": "P0DTD1",
                    "organism": "SARS-CoV-2",
                    "pdbCount": 2422,
                    "preferredDemoPdb": "5R7Y",
                    "pdbIds": ["5R7Y", "5R7Z", "5R80", "5R81", "5R82", "5R83"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "S",
                    "name": "Spike glycoprotein",
                    "uniprot": "P0DTC2",
                    "organism": "SARS-CoV-2",
                    "pdbCount": 1961,
                    "preferredDemoPdb": "6LVN",
                    "pdbIds": ["6LVN", "6LXT", "6LZG", "6M0J", "6M17", "6M1V"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "N",
                    "name": "Nucleoprotein",
                    "uniprot": "P0DTC9",
                    "organism": "SARS-CoV-2",
                    "pdbCount": 90,
                    "preferredDemoPdb": "6M3M",
                    "pdbIds": ["6M3M", "6VYO", "6WJI", "6WKP", "6WZO", "6WZQ"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                }
            ]
        },
        "tuberculosis": {
            "canonicalName": "Tuberculosis",
            "aliases": ["TB", "tuberculosis", "M. tuberculosis", "Mtb"],
            "targetSource": "uniprot + known drug targets (needs SME review)",
            "topTargets": [
                {
                    "geneSymbol": "InhA",
                    "name": "Enoyl-[acyl-carrier-protein] reductase [NADH]",
                    "uniprot": "P9WGR1",
                    "organism": "M. tuberculosis",
                    "pdbCount": 117,
                    "preferredDemoPdb": "1BVR",
                    "pdbIds": ["1BVR", "1ENY", "1ENZ", "1P44", "1P45", "1ZID"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "KasA",
                    "name": "Beta-ketoacyl-ACP synthase KasA",
                    "uniprot": "P9WQD1",
                    "organism": "M. tuberculosis",
                    "pdbCount": 0,
                    "pdbIds": [],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "alphafold",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "DprE1",
                    "name": "Decaprenylphosphoryl-beta-D-ribose oxidase",
                    "uniprot": "P9WJF1",
                    "organism": "M. tuberculosis",
                    "pdbCount": 27,
                    "preferredDemoPdb": "4FDN",
                    "pdbIds": ["4FDN", "4FDO", "4FDP", "4FEH", "4FF6", "4KW5"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "MmpL3",
                    "name": "Mycolic acid transporter MmpL3",
                    "uniprot": "I6Y0L0",
                    "organism": "M. tuberculosis",
                    "pdbCount": 0,
                    "pdbIds": [],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "alphafold",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "GyrB",
                    "name": "DNA gyrase subunit B",
                    "uniprot": "P9WG49",
                    "organism": "M. tuberculosis",
                    "pdbCount": 7,
                    "preferredDemoPdb": "5D5H",
                    "pdbIds": ["5D5H", "5UJ1", "5UJY", "6CQ2", "6CQI", "8CZQ"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                }
            ]
        },
        "alzheimer": {
            "canonicalName": "Alzheimer disease",
            "aliases": [
                "Alzheimer's",
                "Alzheimer's disease",
                "Alzheimer disease",
                "Alzheimer",
                "Alzheimers",
                "alzheimer dementia"
            ],
            "efoId": "MONDO_0004975",
            "targetSource": "open_targets_api",
            "totalAssociatedTargets": 13142,
            "topTargets": [
                {
                    "geneSymbol": "APP",
                    "name": "amyloid beta precursor protein",
                    "uniprot": "P05067",
                    "organism": "Homo sapiens",
                    "pdbCount": 227,
                    "pdbIds": [
                        "1AAP",
                        "1AMB",
                        "1AMC",
                        "1AML",
                        "1BA4"
                    ],
                    "associationScore": 0.8697,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "PSEN1",
                    "name": "presenilin 1",
                    "uniprot": "P49768",
                    "organism": "Homo sapiens",
                    "pdbCount": 26,
                    "pdbIds": [
                        "2KR6",
                        "4UIS",
                        "5A63",
                        "5FN2",
                        "5FN3"
                    ],
                    "associationScore": 0.8663,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "PSEN2",
                    "name": "presenilin 2",
                    "uniprot": "P49810",
                    "organism": "Homo sapiens",
                    "pdbCount": 2,
                    "pdbIds": [
                        "7Y5X",
                        "7Y5Z"
                    ],
                    "associationScore": 0.8165,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "APOE",
                    "name": "apolipoprotein E",
                    "uniprot": "P02649",
                    "organism": "Homo sapiens",
                    "pdbCount": 29,
                    "pdbIds": [
                        "1B68",
                        "1BZ4",
                        "1EA8",
                        "1GS9",
                        "1H7I"
                    ],
                    "associationScore": 0.7749,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "GRIN1",
                    "name": "glutamate ionotropic receptor NMDA type subunit 1",
                    "uniprot": "Q05586",
                    "organism": "Homo sapiens",
                    "pdbCount": 68,
                    "pdbIds": [
                        "2HQW",
                        "2NR1",
                        "3BYA",
                        "5H8F",
                        "5H8H"
                    ],
                    "associationScore": 0.7,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "SORL1",
                    "name": "sortilin related receptor 1",
                    "uniprot": "Q92673",
                    "organism": "Homo sapiens",
                    "pdbCount": 7,
                    "pdbIds": [
                        "2DM4",
                        "3G2S",
                        "3G2T",
                        "3WSX",
                        "3WSY"
                    ],
                    "associationScore": 0.6887,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "ADAM10",
                    "name": "ADAM metallopeptidase domain 10",
                    "uniprot": "O14672",
                    "organism": "Homo sapiens",
                    "pdbCount": 3,
                    "pdbIds": [
                        "6BDZ",
                        "6BE6",
                        "8ESV"
                    ],
                    "associationScore": 0.6817,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "CDK5",
                    "name": "cyclin dependent kinase 5",
                    "uniprot": "Q00535",
                    "organism": "Homo sapiens",
                    "pdbCount": 10,
                    "pdbIds": [
                        "1H4L",
                        "1UNG",
                        "1UNH",
                        "1UNL",
                        "3O0G"
                    ],
                    "associationScore": 0.68,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                }
            ]
        },
        "parkinson": {
            "canonicalName": "Parkinson disease",
            "aliases": [
                "Parkinson's",
                "Parkinson's disease",
                "Parkinson disease",
                "Parkinson",
                "Parkinsons"
            ],
            "efoId": "MONDO_0005180",
            "targetSource": "open_targets_api",
            "totalAssociatedTargets": 7439,
            "topTargets": [
                {
                    "geneSymbol": "LRRK2",
                    "name": "leucine rich repeat kinase 2",
                    "uniprot": "Q5S007",
                    "organism": "Homo sapiens",
                    "pdbCount": 44,
                    "preferredDemoPdb": "2ZEJ",
                    "preferredDemoPdbNote": "ROC domain dimer with GTP analog bound (RCSB nonpolymer_entity_count=2, verified 2026-04-15). Gives BMaps an auto-detected binding site.",
                    "pdbIds": [
                        "2ZEJ",
                        "3D6T",
                        "5MY9",
                        "5MYC",
                        "6DLO"
                    ],
                    "associationScore": 0.8802,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "SNCA",
                    "name": "synuclein alpha",
                    "uniprot": "P37840",
                    "organism": "Homo sapiens",
                    "pdbCount": 165,
                    "pdbIds": [
                        "1XQ8",
                        "2JN5",
                        "2KKW",
                        "2M55",
                        "2N0A"
                    ],
                    "associationScore": 0.8748,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "PINK1",
                    "name": "PTEN induced kinase 1",
                    "uniprot": "Q9BXM7",
                    "organism": "Homo sapiens",
                    "pdbCount": 3,
                    "pdbIds": [
                        "9EIH",
                        "9EII",
                        "9EIJ"
                    ],
                    "associationScore": 0.8606,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "PRKN",
                    "name": "parkin RBR E3 ubiquitin protein ligase",
                    "uniprot": "O60260",
                    "organism": "Homo sapiens",
                    "pdbCount": 21,
                    "pdbIds": [
                        "1IYF",
                        "2JMO",
                        "4BM9",
                        "4I1F",
                        "4I1H"
                    ],
                    "associationScore": 0.8574,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "ATP13A2",
                    "name": "ATPase cation transporting 13A2",
                    "uniprot": "Q9NQ11",
                    "organism": "Homo sapiens",
                    "pdbCount": 25,
                    "pdbIds": [
                        "7FJM",
                        "7FJP",
                        "7FJQ",
                        "7M5V",
                        "7M5X"
                    ],
                    "associationScore": 0.8574,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "DNAJC6",
                    "name": "DnaJ heat shock protein family (Hsp40) member C6",
                    "uniprot": "O75061",
                    "organism": "Homo sapiens",
                    "pdbCount": 0,
                    "pdbIds": [],
                    "associationScore": 0.825,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "alphafold",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "PARK7",
                    "name": "Parkinsonism associated deglycase",
                    "uniprot": "Q99497",
                    "organism": "Homo sapiens",
                    "pdbCount": 82,
                    "pdbIds": [
                        "1J42",
                        "1P5F",
                        "1PDV",
                        "1PDW",
                        "1PE0"
                    ],
                    "associationScore": 0.8187,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "FBXO7",
                    "name": "F-box protein 7",
                    "uniprot": "Q9Y3I1",
                    "organism": "Homo sapiens",
                    "pdbCount": 2,
                    "pdbIds": [
                        "4L9C",
                        "4L9H"
                    ],
                    "associationScore": 0.8021,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                }
            ]
        },
        "type_2_diabetes": {
            "canonicalName": "type 2 diabetes mellitus",
            "aliases": [
                "type 2 diabetes",
                "type II diabetes",
                "T2D",
                "T2DM",
                "diabetes mellitus type 2",
                "adult-onset diabetes",
                "non-insulin-dependent diabetes mellitus",
                "NIDDM"
            ],
            "efoId": "MONDO_0005148",
            "targetSource": "open_targets_api",
            "totalAssociatedTargets": 9957,
            "topTargets": [
                {
                    "geneSymbol": "KCNJ11",
                    "name": "potassium inwardly rectifying channel subfamily J member 11",
                    "uniprot": "Q14654",
                    "organism": "Homo sapiens",
                    "pdbCount": 8,
                    "pdbIds": [
                        "6C3O",
                        "6C3P",
                        "7S5T",
                        "7S5X",
                        "7S5Y"
                    ],
                    "associationScore": 0.8651,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "ABCC8",
                    "name": "ATP binding cassette subfamily C member 8",
                    "uniprot": "Q09428",
                    "organism": "Homo sapiens",
                    "pdbCount": 8,
                    "pdbIds": [
                        "6C3O",
                        "6C3P",
                        "7S5V",
                        "7S5X",
                        "7S5Y"
                    ],
                    "associationScore": 0.8648,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "GCK",
                    "name": "glucokinase",
                    "uniprot": "P35557",
                    "organism": "Homo sapiens",
                    "pdbCount": 35,
                    "preferredDemoPdb": "1V4S",
                    "preferredDemoPdbNote": "Human glucokinase crystal with bound ligands (RCSB nonpolymer_entity_count=3, verified 2026-04-15). Gives BMaps an auto-detected binding site.",
                    "pdbIds": [
                        "1V4S",
                        "1V4T",
                        "3A0I",
                        "3F9M",
                        "3FGU"
                    ],
                    "associationScore": 0.8612,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "PPARG",
                    "name": "peroxisome proliferator activated receptor gamma",
                    "uniprot": "P37231",
                    "organism": "Homo sapiens",
                    "pdbCount": 316,
                    "pdbIds": [
                        "1FM6",
                        "1FM9",
                        "1I7I",
                        "1K74",
                        "1KNU"
                    ],
                    "associationScore": 0.8486,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "INSR",
                    "name": "insulin receptor",
                    "uniprot": "P06213",
                    "organism": "Homo sapiens",
                    "pdbCount": 76,
                    "pdbIds": [
                        "1GAG",
                        "1I44",
                        "1IR3",
                        "1IRK",
                        "1P14"
                    ],
                    "associationScore": 0.7887,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "HNF1B",
                    "name": "HNF1 homeobox B",
                    "uniprot": "P35680",
                    "organism": "Homo sapiens",
                    "pdbCount": 3,
                    "pdbIds": [
                        "2DA6",
                        "2H8R",
                        "5K9S"
                    ],
                    "associationScore": 0.7846,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "HNF1A",
                    "name": "HNF1 homeobox A",
                    "uniprot": "P20823",
                    "organism": "Homo sapiens",
                    "pdbCount": 6,
                    "pdbIds": [
                        "1IC8",
                        "2GYP",
                        "8PI7",
                        "8PI8",
                        "8PI9"
                    ],
                    "associationScore": 0.7796,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "HNF4A",
                    "name": "hepatocyte nuclear factor 4 alpha",
                    "uniprot": "P41235",
                    "organism": "Homo sapiens",
                    "pdbCount": 8,
                    "pdbIds": [
                        "1PZL",
                        "3CBB",
                        "3FS1",
                        "4B7W",
                        "4IQR"
                    ],
                    "associationScore": 0.7763,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                }
            ]
        },
        "breast_cancer": {
            "canonicalName": "breast carcinoma",
            "aliases": [
                "breast cancer",
                "breast tumor",
                "breast tumour",
                "BRCA-positive breast cancer",
                "triple-negative breast cancer",
                "TNBC",
                "HER2-positive breast cancer",
                "ER-positive breast cancer"
            ],
            "efoId": "EFO_0000305",
            "targetSource": "open_targets_api",
            "totalAssociatedTargets": 16001,
            "topTargets": [
                {
                    "geneSymbol": "BRCA2",
                    "name": "BRCA2 DNA repair associated",
                    "uniprot": "P51587",
                    "organism": "Homo sapiens",
                    "pdbCount": 14,
                    "pdbIds": [
                        "1N0W",
                        "3EU7",
                        "6GY2",
                        "6HQU",
                        "7BDX"
                    ],
                    "associationScore": 0.8923,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "BRCA1",
                    "name": "BRCA1 DNA repair associated",
                    "uniprot": "P38398",
                    "organism": "Homo sapiens",
                    "pdbCount": 32,
                    "pdbIds": [
                        "1JM7",
                        "1JNX",
                        "1N5O",
                        "1OQA",
                        "1T15"
                    ],
                    "associationScore": 0.8765,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "PALB2",
                    "name": "partner and localizer of BRCA2",
                    "uniprot": "Q86YC2",
                    "organism": "Homo sapiens",
                    "pdbCount": 4,
                    "pdbIds": [
                        "2W18",
                        "3EU7",
                        "7S4A",
                        "8YAP"
                    ],
                    "associationScore": 0.8667,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "TP53",
                    "name": "tumor protein p53",
                    "uniprot": "P04637",
                    "organism": "Homo sapiens",
                    "pdbCount": 286,
                    "pdbIds": [
                        "1A1U",
                        "1AIE",
                        "1C26",
                        "1DT7",
                        "1GZH"
                    ],
                    "associationScore": 0.8613,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "PIK3CA",
                    "name": "phosphatidylinositol-4,5-bisphosphate 3-kinase catalytic subunit alpha",
                    "uniprot": "P42336",
                    "organism": "Homo sapiens",
                    "pdbCount": 117,
                    "pdbIds": [
                        "2ENQ",
                        "2RD0",
                        "3HHM",
                        "3HIZ",
                        "3ZIM"
                    ],
                    "associationScore": 0.8503,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "CHEK2",
                    "name": "checkpoint kinase 2",
                    "uniprot": "O96017",
                    "organism": "Homo sapiens",
                    "pdbCount": 38,
                    "pdbIds": [
                        "1GXC",
                        "2CN5",
                        "2CN8",
                        "2W0J",
                        "2W7X"
                    ],
                    "associationScore": 0.84,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "ATM",
                    "name": "ATM serine/threonine kinase",
                    "uniprot": "Q13315",
                    "organism": "Homo sapiens",
                    "pdbCount": 14,
                    "pdbIds": [
                        "5NP0",
                        "5NP1",
                        "6HKA",
                        "6K9K",
                        "6K9L"
                    ],
                    "associationScore": 0.8346,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "RAD51C",
                    "name": "RAD51 paralog C",
                    "uniprot": "O43502",
                    "organism": "Homo sapiens",
                    "pdbCount": 4,
                    "pdbIds": [
                        "8FAZ",
                        "8GBJ",
                        "8OUY",
                        "8OUZ"
                    ],
                    "associationScore": 0.8263,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                }
            ]
        },
        "nsclc": {
            "canonicalName": "non-small cell lung carcinoma",
            "aliases": [
                "NSCLC",
                "non-small cell lung cancer",
                "non small cell lung cancer",
                "non-small-cell lung cancer",
                "non-small-cell lung carcinoma",
                "lung adenocarcinoma",
                "lung squamous cell carcinoma"
            ],
            "efoId": "EFO_0003060",
            "targetSource": "open_targets_api",
            "totalAssociatedTargets": 13431,
            "topTargets": [
                {
                    "geneSymbol": "EGFR",
                    "name": "epidermal growth factor receptor",
                    "uniprot": "P00533",
                    "organism": "Homo sapiens",
                    "pdbCount": 351,
                    "pdbIds": [
                        "1IVO",
                        "1M14",
                        "1M17",
                        "1MOX",
                        "1NQL"
                    ],
                    "associationScore": 0.8879,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "KRAS",
                    "name": "KRAS proto-oncogene, GTPase",
                    "uniprot": "P01116",
                    "organism": "Homo sapiens",
                    "pdbCount": 402,
                    "pdbIds": [
                        "1D8D",
                        "1D8E",
                        "1KZO",
                        "1KZP",
                        "1N4P"
                    ],
                    "associationScore": 0.8421,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "ALK",
                    "name": "ALK receptor tyrosine kinase",
                    "uniprot": "Q9UM73",
                    "organism": "Homo sapiens",
                    "pdbCount": 79,
                    "pdbIds": [
                        "2KUP",
                        "2KUQ",
                        "2XB7",
                        "2XBA",
                        "2XP2"
                    ],
                    "associationScore": 0.8119,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "ERBB2",
                    "name": "erb-b2 receptor tyrosine kinase 2",
                    "uniprot": "P04626",
                    "organism": "Homo sapiens",
                    "pdbCount": 56,
                    "pdbIds": [
                        "1MFG",
                        "1MFL",
                        "1MW4",
                        "1N8Z",
                        "1QR1"
                    ],
                    "associationScore": 0.8107,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "ROS1",
                    "name": "ROS proto-oncogene 1, receptor tyrosine kinase",
                    "uniprot": "P08922",
                    "organism": "Homo sapiens",
                    "pdbCount": 5,
                    "pdbIds": [
                        "3ZBF",
                        "4UXL",
                        "7Z5W",
                        "7Z5X",
                        "9QEK"
                    ],
                    "associationScore": 0.8098,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "MET",
                    "name": "MET proto-oncogene, receptor tyrosine kinase",
                    "uniprot": "P08581",
                    "organism": "Homo sapiens",
                    "pdbCount": 120,
                    "pdbIds": [
                        "1FYR",
                        "1R0P",
                        "1R1W",
                        "1SHY",
                        "1SSL"
                    ],
                    "associationScore": 0.8059,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "DICER1",
                    "name": "dicer 1, ribonuclease III",
                    "uniprot": "Q9UPY3",
                    "organism": "Homo sapiens",
                    "pdbCount": 16,
                    "pdbIds": [
                        "2EB1",
                        "4NGB",
                        "4NGC",
                        "4NGD",
                        "4NGF"
                    ],
                    "associationScore": 0.7802,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "BRAF",
                    "name": "B-Raf proto-oncogene, serine/threonine kinase",
                    "uniprot": "P15056",
                    "organism": "Homo sapiens",
                    "pdbCount": 127,
                    "pdbIds": [
                        "1UWH",
                        "1UWJ",
                        "2FB8",
                        "2L05",
                        "3C4C"
                    ],
                    "associationScore": 0.7784,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                }
            ]
        },
        "colorectal_cancer": {
            "canonicalName": "colorectal cancer",
            "aliases": [
                "CRC",
                "colon cancer",
                "rectal cancer",
                "bowel cancer",
                "colorectal carcinoma",
                "colorectal adenocarcinoma"
            ],
            "efoId": "MONDO_0005575",
            "targetSource": "open_targets_api",
            "totalAssociatedTargets": 15818,
            "topTargets": [
                {
                    "geneSymbol": "MSH2",
                    "name": "mutS homolog 2",
                    "uniprot": "P43246",
                    "organism": "Homo sapiens",
                    "pdbCount": 30,
                    "pdbIds": [
                        "2O8B",
                        "2O8C",
                        "2O8D",
                        "2O8E",
                        "2O8F"
                    ],
                    "associationScore": 0.9236,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "MSH6",
                    "name": "mutS homolog 6",
                    "uniprot": "P52701",
                    "organism": "Homo sapiens",
                    "pdbCount": 8,
                    "pdbIds": [
                        "2GFU",
                        "2O8B",
                        "2O8C",
                        "2O8D",
                        "2O8E"
                    ],
                    "associationScore": 0.916,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "MLH1",
                    "name": "mutL homolog 1",
                    "uniprot": "P40692",
                    "organism": "Homo sapiens",
                    "pdbCount": 7,
                    "pdbIds": [
                        "3RBN",
                        "4P7A",
                        "5U5P",
                        "6WBA",
                        "6WBB"
                    ],
                    "associationScore": 0.9143,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "PMS2",
                    "name": "PMS1 homolog 2, mismatch repair system component",
                    "uniprot": "P54278",
                    "organism": "Homo sapiens",
                    "pdbCount": 8,
                    "pdbIds": [
                        "1EA6",
                        "1H7S",
                        "1H7U",
                        "5U5R",
                        "6MFQ"
                    ],
                    "associationScore": 0.8824,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "APC",
                    "name": "APC regulator of Wnt signaling pathway",
                    "uniprot": "P25054",
                    "organism": "Homo sapiens",
                    "pdbCount": 31,
                    "pdbIds": [
                        "1DEB",
                        "1EMU",
                        "1JPP",
                        "1M5I",
                        "1T08"
                    ],
                    "associationScore": 0.8589,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "BRAF",
                    "name": "B-Raf proto-oncogene, serine/threonine kinase",
                    "uniprot": "P15056",
                    "organism": "Homo sapiens",
                    "pdbCount": 127,
                    "pdbIds": [
                        "1UWH",
                        "1UWJ",
                        "2FB8",
                        "2L05",
                        "3C4C"
                    ],
                    "associationScore": 0.8339,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "TP53",
                    "name": "tumor protein p53",
                    "uniprot": "P04637",
                    "organism": "Homo sapiens",
                    "pdbCount": 286,
                    "pdbIds": [
                        "1A1U",
                        "1AIE",
                        "1C26",
                        "1DT7",
                        "1GZH"
                    ],
                    "associationScore": 0.8322,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "PIK3CA",
                    "name": "phosphatidylinositol-4,5-bisphosphate 3-kinase catalytic subunit alpha",
                    "uniprot": "P42336",
                    "organism": "Homo sapiens",
                    "pdbCount": 117,
                    "pdbIds": [
                        "2ENQ",
                        "2RD0",
                        "3HHM",
                        "3HIZ",
                        "3ZIM"
                    ],
                    "associationScore": 0.8175,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                }
            ]
        },
        "rheumatoid_arthritis": {
            "canonicalName": "rheumatoid arthritis",
            "aliases": [
                "RA",
                "rheumatoid disease"
            ],
            "efoId": "EFO_0000685",
            "targetSource": "open_targets_api",
            "totalAssociatedTargets": 7676,
            "topTargets": [
                {
                    "geneSymbol": "TYK2",
                    "name": "tyrosine kinase 2",
                    "uniprot": "P29597",
                    "organism": "Homo sapiens",
                    "pdbCount": 52,
                    "preferredDemoPdb": "3LXN",
                    "preferredDemoPdbNote": "TYK2 kinase domain in complex with tofacitinib (CP-690550) or CMP-6 JAK inhibitor (RCSB nonpolymer_entity_count=1, verified 2026-04-15). Clean JAK-inhibitor co-crystal — classic drug-bound kinase structure.",
                    "pdbIds": [
                        "3LXN",
                        "3LXP",
                        "3NYX",
                        "3NZ0",
                        "3ZON"
                    ],
                    "associationScore": 0.7518,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "IL12B",
                    "name": "interleukin 12B",
                    "uniprot": "P29460",
                    "organism": "Homo sapiens",
                    "pdbCount": 20,
                    "pdbIds": [
                        "1F42",
                        "1F45",
                        "3D85",
                        "3D87",
                        "3DUH"
                    ],
                    "associationScore": 0.7328,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "IL6R",
                    "name": "interleukin 6 receptor",
                    "uniprot": "P08887",
                    "organism": "Homo sapiens",
                    "pdbCount": 10,
                    "pdbIds": [
                        "1N26",
                        "1P9M",
                        "2ARW",
                        "5FUC",
                        "7DC8"
                    ],
                    "associationScore": 0.7325,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "TNF",
                    "name": "tumor necrosis factor",
                    "uniprot": "P01375",
                    "organism": "Homo sapiens",
                    "pdbCount": 49,
                    "pdbIds": [
                        "1A8M",
                        "1TNF",
                        "2AZ5",
                        "2E7A",
                        "2TUN"
                    ],
                    "associationScore": 0.7166,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "TRAF3IP2",
                    "name": "TRAF3 interacting protein 2",
                    "uniprot": "O43734",
                    "organism": "Homo sapiens",
                    "pdbCount": 0,
                    "pdbIds": [],
                    "associationScore": 0.7139,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "alphafold",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "MIF",
                    "name": "macrophage migration inhibitory factor",
                    "uniprot": "P14174",
                    "organism": "Homo sapiens",
                    "pdbCount": 112,
                    "pdbIds": [
                        "1CA7",
                        "1CGQ",
                        "1GCZ",
                        "1GD0",
                        "1GIF"
                    ],
                    "associationScore": 0.6977,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "JAK2",
                    "name": "Janus kinase 2",
                    "uniprot": "O60674",
                    "organism": "Homo sapiens",
                    "pdbCount": 162,
                    "pdbIds": [
                        "2B7A",
                        "2W1I",
                        "2XA4",
                        "3E62",
                        "3E63"
                    ],
                    "associationScore": 0.6934,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "JAK1",
                    "name": "Janus kinase 1",
                    "uniprot": "P23458",
                    "organism": "Homo sapiens",
                    "pdbCount": 51,
                    "pdbIds": [
                        "3EYG",
                        "3EYH",
                        "4E4L",
                        "4E4N",
                        "4E5W"
                    ],
                    "associationScore": 0.6904,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                }
            ]
        },
        "psoriasis": {
            "canonicalName": "psoriasis",
            "aliases": [
                "plaque psoriasis",
                "psoriatic disease",
                "guttate psoriasis",
                "psoriasis vulgaris"
            ],
            "efoId": "EFO_0000676",
            "targetSource": "open_targets_api",
            "totalAssociatedTargets": 7335,
            "topTargets": [
                {
                    "geneSymbol": "CARD14",
                    "name": "caspase recruitment domain family member 14",
                    "uniprot": "Q9BXL6",
                    "organism": "Homo sapiens",
                    "pdbCount": 0,
                    "pdbIds": [],
                    "associationScore": 0.8441,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "alphafold",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "IL36RN",
                    "name": "interleukin 36 receptor antagonist",
                    "uniprot": "Q9UBH0",
                    "organism": "Homo sapiens",
                    "pdbCount": 3,
                    "pdbIds": [
                        "4P0J",
                        "4P0K",
                        "4P0L"
                    ],
                    "associationScore": 0.8206,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "IL12B",
                    "name": "interleukin 12B",
                    "uniprot": "P29460",
                    "organism": "Homo sapiens",
                    "pdbCount": 20,
                    "pdbIds": [
                        "1F42",
                        "1F45",
                        "3D85",
                        "3D87",
                        "3DUH"
                    ],
                    "associationScore": 0.7935,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "TYK2",
                    "name": "tyrosine kinase 2",
                    "uniprot": "P29597",
                    "organism": "Homo sapiens",
                    "pdbCount": 52,
                    "pdbIds": [
                        "3LXN",
                        "3LXP",
                        "3NYX",
                        "3NZ0",
                        "3ZON"
                    ],
                    "associationScore": 0.7409,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "TRAF3IP2",
                    "name": "TRAF3 interacting protein 2",
                    "uniprot": "O43734",
                    "organism": "Homo sapiens",
                    "pdbCount": 0,
                    "pdbIds": [],
                    "associationScore": 0.7053,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "alphafold",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "IL17RA",
                    "name": "interleukin 17 receptor A",
                    "uniprot": "Q96F46",
                    "organism": "Homo sapiens",
                    "pdbCount": 9,
                    "pdbIds": [
                        "3JVF",
                        "4HSA",
                        "4NUX",
                        "5N9B",
                        "5NAN"
                    ],
                    "associationScore": 0.6824,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "IL23A",
                    "name": "interleukin 23 subunit alpha",
                    "uniprot": "Q9NPF7",
                    "organism": "Homo sapiens",
                    "pdbCount": 15,
                    "pdbIds": [
                        "3D85",
                        "3D87",
                        "3DUH",
                        "3QWR",
                        "4GRW"
                    ],
                    "associationScore": 0.6773,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "AP1S3",
                    "name": "adaptor related protein complex 1 subunit sigma 3",
                    "uniprot": "Q96PC3",
                    "organism": "Homo sapiens",
                    "pdbCount": 20,
                    "pdbIds": [
                        "4HMY",
                        "6CM9",
                        "6CRI",
                        "6D83",
                        "6D84"
                    ],
                    "associationScore": 0.6685,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                }
            ]
        },
        "crohns_disease": {
            "canonicalName": "Crohn's disease",
            "aliases": [
                "Crohn disease",
                "Crohns disease",
                "Crohn's",
                "Crohns",
                "regional enteritis",
                "regional ileitis"
            ],
            "efoId": "EFO_0000384",
            "targetSource": "open_targets_api",
            "totalAssociatedTargets": 6569,
            "topTargets": [
                {
                    "geneSymbol": "NOD2",
                    "name": "nucleotide binding oligomerization domain containing 2",
                    "uniprot": "Q9HC29",
                    "organism": "Homo sapiens",
                    "pdbCount": 0,
                    "pdbIds": [],
                    "associationScore": 0.7919,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "alphafold",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "IL12B",
                    "name": "interleukin 12B",
                    "uniprot": "P29460",
                    "organism": "Homo sapiens",
                    "pdbCount": 20,
                    "pdbIds": [
                        "1F42",
                        "1F45",
                        "3D85",
                        "3D87",
                        "3DUH"
                    ],
                    "associationScore": 0.7365,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "ITGA4",
                    "name": "integrin subunit alpha 4",
                    "uniprot": "P13612",
                    "organism": "Homo sapiens",
                    "pdbCount": 5,
                    "pdbIds": [
                        "3V4P",
                        "3V4V",
                        "4HKC",
                        "5C7Z",
                        "5FPI"
                    ],
                    "associationScore": 0.7135,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "IL10",
                    "name": "interleukin 10",
                    "uniprot": "P22301",
                    "organism": "Homo sapiens",
                    "pdbCount": 9,
                    "pdbIds": [
                        "1ILK",
                        "1INR",
                        "1J7V",
                        "1LK3",
                        "1Y6K"
                    ],
                    "associationScore": 0.641,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "TNF",
                    "name": "tumor necrosis factor",
                    "uniprot": "P01375",
                    "organism": "Homo sapiens",
                    "pdbCount": 49,
                    "pdbIds": [
                        "1A8M",
                        "1TNF",
                        "2AZ5",
                        "2E7A",
                        "2TUN"
                    ],
                    "associationScore": 0.6281,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "IL23A",
                    "name": "interleukin 23 subunit alpha",
                    "uniprot": "Q9NPF7",
                    "organism": "Homo sapiens",
                    "pdbCount": 15,
                    "pdbIds": [
                        "3D85",
                        "3D87",
                        "3DUH",
                        "3QWR",
                        "4GRW"
                    ],
                    "associationScore": 0.6194,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "NR3C1",
                    "name": "nuclear receptor subfamily 3 group C member 1",
                    "uniprot": "P04150",
                    "organism": "Homo sapiens",
                    "pdbCount": 58,
                    "pdbIds": [
                        "1M2Z",
                        "1NHZ",
                        "1P93",
                        "3BQD",
                        "3CLD"
                    ],
                    "associationScore": 0.6096,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "ITGB7",
                    "name": "integrin subunit beta 7",
                    "uniprot": "P26010",
                    "organism": "Homo sapiens",
                    "pdbCount": 4,
                    "pdbIds": [
                        "2BRQ",
                        "3V4P",
                        "3V4V",
                        "8ZJF"
                    ],
                    "associationScore": 0.6096,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                }
            ]
        },
        "ulcerative_colitis": {
            "canonicalName": "ulcerative colitis",
            "aliases": [
                "UC",
                "ulcerative colitis"
            ],
            "efoId": "EFO_0000729",
            "targetSource": "open_targets_api",
            "totalAssociatedTargets": 9424,
            "topTargets": [
                {
                    "geneSymbol": "IL12B",
                    "name": "interleukin 12B",
                    "uniprot": "P29460",
                    "organism": "Homo sapiens",
                    "pdbCount": 20,
                    "pdbIds": [
                        "1F42",
                        "1F45",
                        "3D85",
                        "3D87",
                        "3DUH"
                    ],
                    "associationScore": 0.7346,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "JAK2",
                    "name": "Janus kinase 2",
                    "uniprot": "O60674",
                    "organism": "Homo sapiens",
                    "pdbCount": 162,
                    "pdbIds": [
                        "2B7A",
                        "2W1I",
                        "2XA4",
                        "3E62",
                        "3E63"
                    ],
                    "associationScore": 0.6653,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "TNF",
                    "name": "tumor necrosis factor",
                    "uniprot": "P01375",
                    "organism": "Homo sapiens",
                    "pdbCount": 49,
                    "pdbIds": [
                        "1A8M",
                        "1TNF",
                        "2AZ5",
                        "2E7A",
                        "2TUN"
                    ],
                    "associationScore": 0.6392,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "PPARG",
                    "name": "peroxisome proliferator activated receptor gamma",
                    "uniprot": "P37231",
                    "organism": "Homo sapiens",
                    "pdbCount": 316,
                    "pdbIds": [
                        "1FM6",
                        "1FM9",
                        "1I7I",
                        "1K74",
                        "1KNU"
                    ],
                    "associationScore": 0.6314,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "IL23A",
                    "name": "interleukin 23 subunit alpha",
                    "uniprot": "Q9NPF7",
                    "organism": "Homo sapiens",
                    "pdbCount": 15,
                    "pdbIds": [
                        "3D85",
                        "3D87",
                        "3DUH",
                        "3QWR",
                        "4GRW"
                    ],
                    "associationScore": 0.6293,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "NOD2",
                    "name": "nucleotide binding oligomerization domain containing 2",
                    "uniprot": "Q9HC29",
                    "organism": "Homo sapiens",
                    "pdbCount": 0,
                    "pdbIds": [],
                    "associationScore": 0.6274,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "alphafold",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "PTGS2",
                    "name": "prostaglandin-endoperoxide synthase 2",
                    "uniprot": "P35354",
                    "organism": "Homo sapiens",
                    "pdbCount": 7,
                    "pdbIds": [
                        "5F19",
                        "5F1A",
                        "5IKQ",
                        "5IKR",
                        "5IKT"
                    ],
                    "associationScore": 0.6259,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "NR3C1",
                    "name": "nuclear receptor subfamily 3 group C member 1",
                    "uniprot": "P04150",
                    "organism": "Homo sapiens",
                    "pdbCount": 58,
                    "pdbIds": [
                        "1M2Z",
                        "1NHZ",
                        "1P93",
                        "3BQD",
                        "3CLD"
                    ],
                    "associationScore": 0.6183,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                }
            ]
        },
        "multiple_sclerosis": {
            "canonicalName": "multiple sclerosis",
            "aliases": [
                "MS",
                "relapsing-remitting MS",
                "RRMS",
                "primary progressive MS",
                "PPMS",
                "demyelinating disease"
            ],
            "efoId": "MONDO_0005301",
            "targetSource": "open_targets_api",
            "totalAssociatedTargets": 4823,
            "topTargets": [
                {
                    "geneSymbol": "NR3C1",
                    "name": "nuclear receptor subfamily 3 group C member 1",
                    "uniprot": "P04150",
                    "organism": "Homo sapiens",
                    "pdbCount": 58,
                    "pdbIds": [
                        "1M2Z",
                        "1NHZ",
                        "1P93",
                        "3BQD",
                        "3CLD"
                    ],
                    "associationScore": 0.6842,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "IL2RA",
                    "name": "interleukin 2 receptor subunit alpha",
                    "uniprot": "P01589",
                    "organism": "Homo sapiens",
                    "pdbCount": 9,
                    "pdbIds": [
                        "1Z92",
                        "2B5I",
                        "2ERJ",
                        "3IU3",
                        "3NFP"
                    ],
                    "associationScore": 0.6647,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "TNFRSF1A",
                    "name": "TNF receptor superfamily member 1A",
                    "uniprot": "P19438",
                    "organism": "Homo sapiens",
                    "pdbCount": 11,
                    "pdbIds": [
                        "1EXT",
                        "1FT4",
                        "1ICH",
                        "1NCF",
                        "1TNR"
                    ],
                    "associationScore": 0.6569,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "KEAP1",
                    "name": "kelch like ECH associated protein 1",
                    "uniprot": "Q14145",
                    "organism": "Homo sapiens",
                    "pdbCount": 109,
                    "pdbIds": [
                        "1U6D",
                        "1ZGK",
                        "2FLU",
                        "3VNG",
                        "3VNH"
                    ],
                    "associationScore": 0.642,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "MS4A1",
                    "name": "membrane spanning 4-domains A1",
                    "uniprot": "P11836",
                    "organism": "Homo sapiens",
                    "pdbCount": 10,
                    "pdbIds": [
                        "2OSL",
                        "3BKY",
                        "3PP4",
                        "6VJA",
                        "6Y90"
                    ],
                    "associationScore": 0.6228,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "S1PR1",
                    "name": "sphingosine-1-phosphate receptor 1",
                    "uniprot": "P21453",
                    "organism": "Homo sapiens",
                    "pdbCount": 17,
                    "pdbIds": [
                        "3V2W",
                        "3V2Y",
                        "7EO2",
                        "7EO4",
                        "7EVY"
                    ],
                    "associationScore": 0.6085,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "IFNAR1",
                    "name": "interferon alpha and beta receptor subunit 1",
                    "uniprot": "P17181",
                    "organism": "Homo sapiens",
                    "pdbCount": 5,
                    "pdbIds": [
                        "3S98",
                        "3SE3",
                        "3SE4",
                        "4PO6",
                        "8Y31"
                    ],
                    "associationScore": 0.6064,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "S1PR5",
                    "name": "sphingosine-1-phosphate receptor 5",
                    "uniprot": "Q9H228",
                    "organism": "Homo sapiens",
                    "pdbCount": 2,
                    "pdbIds": [
                        "7EW1",
                        "7YXA"
                    ],
                    "associationScore": 0.6063,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                }
            ]
        },
        "asthma": {
            "canonicalName": "asthma",
            "aliases": [
                "bronchial asthma",
                "allergic asthma",
                "severe asthma",
                "eosinophilic asthma"
            ],
            "efoId": "MONDO_0004979",
            "targetSource": "open_targets_api",
            "totalAssociatedTargets": 7544,
            "topTargets": [
                {
                    "geneSymbol": "IL4R",
                    "name": "interleukin 4 receptor",
                    "uniprot": "P24394",
                    "organism": "Homo sapiens",
                    "pdbCount": 10,
                    "pdbIds": [
                        "1IAR",
                        "1IRS",
                        "3BPL",
                        "3BPN",
                        "3BPO"
                    ],
                    "associationScore": 0.7427,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "FLG",
                    "name": "filaggrin",
                    "uniprot": "P20930",
                    "organism": "Homo sapiens",
                    "pdbCount": 1,
                    "pdbIds": [
                        "4PCW"
                    ],
                    "associationScore": 0.7375,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "ADORA1",
                    "name": "adenosine A1 receptor",
                    "uniprot": "P30542",
                    "organism": "Homo sapiens",
                    "pdbCount": 5,
                    "pdbIds": [
                        "5N2S",
                        "5UEN",
                        "6D9H",
                        "7LD3",
                        "7LD4"
                    ],
                    "associationScore": 0.7238,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "IL33",
                    "name": "interleukin 33",
                    "uniprot": "O95760",
                    "organism": "Homo sapiens",
                    "pdbCount": 2,
                    "pdbIds": [
                        "2KLL",
                        "4KC3"
                    ],
                    "associationScore": 0.7209,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "TSLP",
                    "name": "thymic stromal lymphopoietin",
                    "uniprot": "Q969D9",
                    "organism": "Homo sapiens",
                    "pdbCount": 5,
                    "pdbIds": [
                        "5J11",
                        "5J12",
                        "5J13",
                        "8QFZ",
                        "8ZEU"
                    ],
                    "associationScore": 0.7172,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "PDE4D",
                    "name": "phosphodiesterase 4D",
                    "uniprot": "Q08499",
                    "organism": "Homo sapiens",
                    "pdbCount": 118,
                    "pdbIds": [
                        "1E9K",
                        "1MKD",
                        "1OYN",
                        "1PTW",
                        "1Q9M"
                    ],
                    "associationScore": 0.6857,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "IL13",
                    "name": "interleukin 13",
                    "uniprot": "P35225",
                    "organism": "Homo sapiens",
                    "pdbCount": 13,
                    "pdbIds": [
                        "1GA3",
                        "1IJZ",
                        "1IK0",
                        "3BPO",
                        "3G6D"
                    ],
                    "associationScore": 0.6667,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "IL5",
                    "name": "interleukin 5",
                    "uniprot": "P05113",
                    "organism": "Homo sapiens",
                    "pdbCount": 4,
                    "pdbIds": [
                        "1HUL",
                        "3QT2",
                        "3VA2",
                        "8TLD"
                    ],
                    "associationScore": 0.6432,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                }
            ]
        },
        "copd": {
            "canonicalName": "chronic obstructive pulmonary disease",
            "aliases": [
                "COPD",
                "emphysema",
                "chronic bronchitis",
                "chronic airflow limitation"
            ],
            "efoId": "EFO_0000341",
            "targetSource": "open_targets_api",
            "totalAssociatedTargets": 7591,
            "topTargets": [
                {
                    "geneSymbol": "SCNN1B",
                    "name": "sodium channel epithelial 1 subunit beta",
                    "uniprot": "P51168",
                    "organism": "Homo sapiens",
                    "pdbCount": 5,
                    "pdbIds": [
                        "6BQN",
                        "6WTH",
                        "9BLR",
                        "9BTG",
                        "9BTU"
                    ],
                    "associationScore": 0.8112,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "SCNN1A",
                    "name": "sodium channel epithelial 1 subunit alpha",
                    "uniprot": "P37088",
                    "organism": "Homo sapiens",
                    "pdbCount": 3,
                    "pdbIds": [
                        "2M3O",
                        "6BQN",
                        "6WTH"
                    ],
                    "associationScore": 0.7975,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "CFTR",
                    "name": "CF transmembrane conductance regulator",
                    "uniprot": "P13569",
                    "organism": "Homo sapiens",
                    "pdbCount": 58,
                    "pdbIds": [
                        "1XMI",
                        "1XMJ",
                        "2BBO",
                        "2BBS",
                        "2BBT"
                    ],
                    "associationScore": 0.7662,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "SCNN1G",
                    "name": "sodium channel epithelial 1 subunit gamma",
                    "uniprot": "P51170",
                    "organism": "Homo sapiens",
                    "pdbCount": 5,
                    "pdbIds": [
                        "6BQN",
                        "6WTH",
                        "9BLR",
                        "9BTG",
                        "9BTU"
                    ],
                    "associationScore": 0.7561,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "NR3C1",
                    "name": "nuclear receptor subfamily 3 group C member 1",
                    "uniprot": "P04150",
                    "organism": "Homo sapiens",
                    "pdbCount": 58,
                    "pdbIds": [
                        "1M2Z",
                        "1NHZ",
                        "1P93",
                        "3BQD",
                        "3CLD"
                    ],
                    "associationScore": 0.7064,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "SERPINA1",
                    "name": "serpin family A member 1",
                    "uniprot": "P01009",
                    "organism": "Homo sapiens",
                    "pdbCount": 43,
                    "pdbIds": [
                        "1ATU",
                        "1D5S",
                        "1EZX",
                        "1HP7",
                        "1IZ2"
                    ],
                    "associationScore": 0.6942,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "CHRM3",
                    "name": "cholinergic receptor muscarinic 3",
                    "uniprot": "P20309",
                    "organism": "Homo sapiens",
                    "pdbCount": 5,
                    "pdbIds": [
                        "2CSA",
                        "8E9W",
                        "8E9Y",
                        "8E9Z",
                        "8EA0"
                    ],
                    "associationScore": 0.6748,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "FAM13A",
                    "name": "family with sequence similarity 13 member A",
                    "uniprot": "O94988",
                    "organism": "Homo sapiens",
                    "pdbCount": 0,
                    "pdbIds": [],
                    "associationScore": 0.6516,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "alphafold",
                    "reviewRequired": false
                }
            ]
        },
        "heart_failure": {
            "canonicalName": "heart failure",
            "aliases": [
                "HF",
                "CHF",
                "congestive heart failure",
                "cardiac failure",
                "HFrEF",
                "HFpEF",
                "heart failure with reduced ejection fraction",
                "heart failure with preserved ejection fraction"
            ],
            "efoId": "EFO_0003144",
            "targetSource": "open_targets_api",
            "totalAssociatedTargets": 5812,
            "topTargets": [
                {
                    "geneSymbol": "TTN",
                    "name": "titin",
                    "uniprot": "Q8WZ42",
                    "organism": "Homo sapiens",
                    "pdbCount": 64,
                    "pdbIds": [
                        "1BPV",
                        "1G1C",
                        "1NCT",
                        "1NCU",
                        "1TIT"
                    ],
                    "associationScore": 0.7603,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "HMGCR",
                    "name": "3-hydroxy-3-methylglutaryl-CoA reductase",
                    "uniprot": "P04035",
                    "organism": "Homo sapiens",
                    "pdbCount": 24,
                    "pdbIds": [
                        "1DQ8",
                        "1DQ9",
                        "1DQA",
                        "1HW8",
                        "1HW9"
                    ],
                    "associationScore": 0.6857,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "BMPR2",
                    "name": "bone morphogenetic protein receptor type 2",
                    "uniprot": "Q13873",
                    "organism": "Homo sapiens",
                    "pdbCount": 7,
                    "pdbIds": [
                        "2HLQ",
                        "3G2F",
                        "6UNP",
                        "7PPA",
                        "7PPB"
                    ],
                    "associationScore": 0.6795,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "CACNA1D",
                    "name": "calcium voltage-gated channel subunit alpha1 D",
                    "uniprot": "Q01668",
                    "organism": "Homo sapiens",
                    "pdbCount": 6,
                    "pdbIds": [
                        "3LV3",
                        "7UHF",
                        "7UHG",
                        "8E59",
                        "8E5A"
                    ],
                    "associationScore": 0.6729,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "ATP1B1",
                    "name": "ATPase Na+/K+ transporting subunit beta 1",
                    "uniprot": "P05026",
                    "organism": "Homo sapiens",
                    "pdbCount": 9,
                    "pdbIds": [
                        "7E1Z",
                        "7E20",
                        "7E21",
                        "8D3U",
                        "8D3V"
                    ],
                    "associationScore": 0.6523,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "PDE3A",
                    "name": "phosphodiesterase 3A",
                    "uniprot": "Q14432",
                    "organism": "Homo sapiens",
                    "pdbCount": 9,
                    "pdbIds": [
                        "7EG0",
                        "7EG1",
                        "7EG4",
                        "7KWE",
                        "7L27"
                    ],
                    "associationScore": 0.649,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "GUCY1A1",
                    "name": "guanylate cyclase 1 soluble subunit alpha 1",
                    "uniprot": "Q02108",
                    "organism": "Homo sapiens",
                    "pdbCount": 12,
                    "pdbIds": [
                        "3UVJ",
                        "4NI2",
                        "6JT0",
                        "6JT1",
                        "6JT2"
                    ],
                    "associationScore": 0.6396,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "SLC5A2",
                    "name": "solute carrier family 5 member 2",
                    "uniprot": "P31639",
                    "organism": "Homo sapiens",
                    "pdbCount": 12,
                    "pdbIds": [
                        "7VSI",
                        "7YNJ",
                        "7YNK",
                        "8HB0",
                        "8HDH"
                    ],
                    "associationScore": 0.6306,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                }
            ]
        },
        "hypertension": {
            "canonicalName": "hypertension",
            "aliases": [
                "high blood pressure",
                "HTN",
                "essential hypertension",
                "systemic hypertension"
            ],
            "efoId": "EFO_0000537",
            "targetSource": "open_targets_api",
            "totalAssociatedTargets": 9843,
            "topTargets": [
                {
                    "geneSymbol": "BMPR2",
                    "name": "bone morphogenetic protein receptor type 2",
                    "uniprot": "Q13873",
                    "organism": "Homo sapiens",
                    "pdbCount": 7,
                    "pdbIds": [
                        "2HLQ",
                        "3G2F",
                        "6UNP",
                        "7PPA",
                        "7PPB"
                    ],
                    "associationScore": 0.8527,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "EIF2AK4",
                    "name": "eukaryotic translation initiation factor 2 alpha kinase 4",
                    "uniprot": "Q9P2K8",
                    "organism": "Homo sapiens",
                    "pdbCount": 8,
                    "pdbIds": [
                        "6N3L",
                        "6N3N",
                        "6N3O",
                        "7E2K",
                        "7E2M"
                    ],
                    "associationScore": 0.8138,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "AGTR1",
                    "name": "angiotensin II receptor type 1",
                    "uniprot": "P30556",
                    "organism": "Homo sapiens",
                    "pdbCount": 6,
                    "pdbIds": [
                        "4YAY",
                        "4ZUD",
                        "6DO1",
                        "6OS0",
                        "6OS1"
                    ],
                    "associationScore": 0.8103,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "KCNK3",
                    "name": "potassium two pore domain channel subfamily K member 3",
                    "uniprot": "O14649",
                    "organism": "Homo sapiens",
                    "pdbCount": 4,
                    "pdbIds": [
                        "6RV2",
                        "6RV3",
                        "6RV4",
                        "9G9X"
                    ],
                    "associationScore": 0.807,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "SARS2",
                    "name": "seryl-tRNA synthetase 2, mitochondrial",
                    "uniprot": "Q9NP81",
                    "organism": "Homo sapiens",
                    "pdbCount": 6,
                    "pdbIds": [
                        "7TZB",
                        "7U2A",
                        "7U2B",
                        "7YDF",
                        "7YDG"
                    ],
                    "associationScore": 0.7806,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "SMAD9",
                    "name": "SMAD family member 9",
                    "uniprot": "O15198",
                    "organism": "Homo sapiens",
                    "pdbCount": 1,
                    "pdbIds": [
                        "6FZT"
                    ],
                    "associationScore": 0.7786,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "PKD1",
                    "name": "polycystin 1, transient receptor potential channel interacting",
                    "uniprot": "P98161",
                    "organism": "Homo sapiens",
                    "pdbCount": 10,
                    "pdbIds": [
                        "1B4R",
                        "6A70",
                        "8Z6B",
                        "8Z6F",
                        "8Z6H"
                    ],
                    "associationScore": 0.7766,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "ACE",
                    "name": "angiotensin I converting enzyme",
                    "uniprot": "P12821",
                    "organism": "Homo sapiens",
                    "pdbCount": 78,
                    "pdbIds": [
                        "1O86",
                        "1O8A",
                        "1UZE",
                        "1UZF",
                        "2C6F"
                    ],
                    "associationScore": 0.7572,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                }
            ]
        },
        "obesity": {
            "canonicalName": "obesity",
            "aliases": [
                "morbid obesity",
                "overweight",
                "adiposity"
            ],
            "efoId": "EFO_0001073",
            "targetSource": "open_targets_api",
            "totalAssociatedTargets": 9603,
            "topTargets": [
                {
                    "geneSymbol": "MC4R",
                    "name": "melanocortin 4 receptor",
                    "uniprot": "P32245",
                    "organism": "Homo sapiens",
                    "pdbCount": 12,
                    "pdbIds": [
                        "6W25",
                        "7AUE",
                        "7F53",
                        "7F54",
                        "7F55"
                    ],
                    "associationScore": 0.8378,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "PCSK1",
                    "name": "proprotein convertase subtilisin/kexin type 1",
                    "uniprot": "P29120",
                    "organism": "Homo sapiens",
                    "pdbCount": 3,
                    "pdbIds": [
                        "9FIC",
                        "9FID",
                        "9FIE"
                    ],
                    "associationScore": 0.7927,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "LEPR",
                    "name": "leptin receptor",
                    "uniprot": "P48357",
                    "organism": "Homo sapiens",
                    "pdbCount": 9,
                    "pdbIds": [
                        "3V6O",
                        "6E2P",
                        "7Z3Q",
                        "8AVE",
                        "8AVF"
                    ],
                    "associationScore": 0.773,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "POMC",
                    "name": "proopiomelanocortin",
                    "uniprot": "P01189",
                    "organism": "Homo sapiens",
                    "pdbCount": 13,
                    "pdbIds": [
                        "4XNH",
                        "4XPD",
                        "4Y49",
                        "6TUB",
                        "7F53"
                    ],
                    "associationScore": 0.7559,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "GLP1R",
                    "name": "glucagon like peptide 1 receptor",
                    "uniprot": "P43220",
                    "organism": "Homo sapiens",
                    "pdbCount": 56,
                    "pdbIds": [
                        "3C59",
                        "3C5T",
                        "3IOL",
                        "4ZGM",
                        "5E94"
                    ],
                    "associationScore": 0.7374,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "PPARG",
                    "name": "peroxisome proliferator activated receptor gamma",
                    "uniprot": "P37231",
                    "organism": "Homo sapiens",
                    "pdbCount": 316,
                    "pdbIds": [
                        "1FM6",
                        "1FM9",
                        "1I7I",
                        "1K74",
                        "1KNU"
                    ],
                    "associationScore": 0.7285,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "LEP",
                    "name": "leptin",
                    "uniprot": "P41159",
                    "organism": "Homo sapiens",
                    "pdbCount": 9,
                    "pdbIds": [
                        "1AX8",
                        "7Z3Q",
                        "8AVE",
                        "8AVF",
                        "8AVO"
                    ],
                    "associationScore": 0.7234,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "GIPR",
                    "name": "gastric inhibitory polypeptide receptor",
                    "uniprot": "P48546",
                    "organism": "Homo sapiens",
                    "pdbCount": 13,
                    "pdbIds": [
                        "2QKH",
                        "4HJ0",
                        "6DKJ",
                        "7DTY",
                        "7FIN"
                    ],
                    "associationScore": 0.6986,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                }
            ]
        },
        "chronic_kidney_disease": {
            "canonicalName": "chronic kidney disease",
            "aliases": [
                "CKD",
                "chronic renal disease",
                "chronic kidney failure",
                "chronic renal failure",
                "renal insufficiency"
            ],
            "efoId": "EFO_0003884",
            "targetSource": "open_targets_api",
            "totalAssociatedTargets": 7314,
            "topTargets": [
                {
                    "geneSymbol": "ACE",
                    "name": "angiotensin I converting enzyme",
                    "uniprot": "P12821",
                    "organism": "Homo sapiens",
                    "pdbCount": 78,
                    "pdbIds": [
                        "1O86",
                        "1O8A",
                        "1UZE",
                        "1UZF",
                        "2C6F"
                    ],
                    "associationScore": 0.7672,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "PKD1",
                    "name": "polycystin 1, transient receptor potential channel interacting",
                    "uniprot": "P98161",
                    "organism": "Homo sapiens",
                    "pdbCount": 10,
                    "pdbIds": [
                        "1B4R",
                        "6A70",
                        "8Z6B",
                        "8Z6F",
                        "8Z6H"
                    ],
                    "associationScore": 0.7045,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "PKD2",
                    "name": "polycystin 2, transient receptor potential cation channel",
                    "uniprot": "Q13563",
                    "organism": "Homo sapiens",
                    "pdbCount": 27,
                    "pdbIds": [
                        "2KLD",
                        "2KLE",
                        "2KQ6",
                        "2Y4Q",
                        "3HRN"
                    ],
                    "associationScore": 0.6678,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "UMOD",
                    "name": "uromodulin",
                    "uniprot": "P07911",
                    "organism": "Homo sapiens",
                    "pdbCount": 10,
                    "pdbIds": [
                        "4WRN",
                        "6TQK",
                        "6TQL",
                        "6ZS5",
                        "6ZYA"
                    ],
                    "associationScore": 0.6485,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "SLC12A3",
                    "name": "solute carrier family 12 member 3",
                    "uniprot": "P55017",
                    "organism": "Homo sapiens",
                    "pdbCount": 9,
                    "pdbIds": [
                        "7Y6I",
                        "7YG0",
                        "7YG1",
                        "8FHN",
                        "8FHO"
                    ],
                    "associationScore": 0.6289,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "SLC5A2",
                    "name": "solute carrier family 5 member 2",
                    "uniprot": "P31639",
                    "organism": "Homo sapiens",
                    "pdbCount": 12,
                    "pdbIds": [
                        "7VSI",
                        "7YNJ",
                        "7YNK",
                        "8HB0",
                        "8HDH"
                    ],
                    "associationScore": 0.6275,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "VDR",
                    "name": "vitamin D receptor",
                    "uniprot": "P11473",
                    "organism": "Homo sapiens",
                    "pdbCount": 52,
                    "pdbIds": [
                        "1DB1",
                        "1IE8",
                        "1IE9",
                        "1KB2",
                        "1KB4"
                    ],
                    "associationScore": 0.6252,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "EPOR",
                    "name": "erythropoietin receptor",
                    "uniprot": "P19235",
                    "organism": "Homo sapiens",
                    "pdbCount": 22,
                    "pdbIds": [
                        "1CN4",
                        "1EBA",
                        "1EBP",
                        "1EER",
                        "1ERN"
                    ],
                    "associationScore": 0.6148,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                }
            ]
        },
        "lupus": {
            "canonicalName": "systemic lupus erythematosus",
            "aliases": [
                "lupus",
                "SLE",
                "lupus erythematosus",
                "systemic lupus"
            ],
            "efoId": "MONDO_0007915",
            "targetSource": "open_targets_api",
            "totalAssociatedTargets": 6401,
            "topTargets": [
                {
                    "geneSymbol": "TREX1",
                    "name": "three prime repair exonuclease 1",
                    "uniprot": "Q9NSU2",
                    "organism": "Homo sapiens",
                    "pdbCount": 9,
                    "pdbIds": [
                        "7TQN",
                        "7TQO",
                        "7TQP",
                        "7TQQ",
                        "8VL7"
                    ],
                    "associationScore": 0.7792,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "DNASE1L3",
                    "name": "deoxyribonuclease 1L3",
                    "uniprot": "Q13609",
                    "organism": "Homo sapiens",
                    "pdbCount": 1,
                    "pdbIds": [
                        "7KIU"
                    ],
                    "associationScore": 0.7549,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "TLR7",
                    "name": "toll like receptor 7",
                    "uniprot": "Q9NYK1",
                    "organism": "Homo sapiens",
                    "pdbCount": 1,
                    "pdbIds": [
                        "7CYN"
                    ],
                    "associationScore": 0.7353,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "BLK",
                    "name": "BLK proto-oncogene, Src family tyrosine kinase",
                    "uniprot": "P51451",
                    "organism": "Homo sapiens",
                    "pdbCount": 0,
                    "pdbIds": [],
                    "associationScore": 0.6823,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "alphafold",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "IRF5",
                    "name": "interferon regulatory factor 5",
                    "uniprot": "Q13568",
                    "organism": "Homo sapiens",
                    "pdbCount": 1,
                    "pdbIds": [
                        "3DSH"
                    ],
                    "associationScore": 0.6636,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "PTPN22",
                    "name": "protein tyrosine phosphatase non-receptor type 22",
                    "uniprot": "Q9Y2R2",
                    "organism": "Homo sapiens",
                    "pdbCount": 9,
                    "pdbIds": [
                        "2P6X",
                        "2QCJ",
                        "2QCT",
                        "3BRH",
                        "3H2X"
                    ],
                    "associationScore": 0.6342,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "TYK2",
                    "name": "tyrosine kinase 2",
                    "uniprot": "P29597",
                    "organism": "Homo sapiens",
                    "pdbCount": 52,
                    "pdbIds": [
                        "3LXN",
                        "3LXP",
                        "3NYX",
                        "3NZ0",
                        "3ZON"
                    ],
                    "associationScore": 0.6272,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "TNFSF13B",
                    "name": "TNF superfamily member 13b",
                    "uniprot": "Q9Y275",
                    "organism": "Homo sapiens",
                    "pdbCount": 12,
                    "pdbIds": [
                        "1JH5",
                        "1KD7",
                        "1KXG",
                        "1OQD",
                        "1OQE"
                    ],
                    "associationScore": 0.6231,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                }
            ]
        },
        "sickle_cell": {
            "canonicalName": "sickle cell disease",
            "aliases": [
                "sickle cell anemia",
                "sickle cell anaemia",
                "SCD",
                "sickle cell"
            ],
            "efoId": "MONDO_0011382",
            "targetSource": "open_targets_api",
            "totalAssociatedTargets": 1245,
            "topTargets": [
                {
                    "geneSymbol": "HBB",
                    "name": "hemoglobin subunit beta",
                    "uniprot": "P68871",
                    "organism": "Homo sapiens",
                    "pdbCount": 334,
                    "pdbIds": [
                        "1A00",
                        "1A01",
                        "1A0U",
                        "1A0Z",
                        "1A3N"
                    ],
                    "associationScore": 0.8012,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "RRM2B",
                    "name": "ribonucleotide reductase regulatory TP53 inducible subunit M2B",
                    "uniprot": "Q7LG56",
                    "organism": "Homo sapiens",
                    "pdbCount": 3,
                    "pdbIds": [
                        "2VUX",
                        "3HF1",
                        "4DJN"
                    ],
                    "associationScore": 0.5792,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "RRM1",
                    "name": "ribonucleotide reductase catalytic subunit M1",
                    "uniprot": "P23921",
                    "organism": "Homo sapiens",
                    "pdbCount": 12,
                    "pdbIds": [
                        "2WGH",
                        "3HNC",
                        "3HND",
                        "3HNE",
                        "3HNF"
                    ],
                    "associationScore": 0.5792,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "RRM2",
                    "name": "ribonucleotide reductase regulatory subunit M2",
                    "uniprot": "P31350",
                    "organism": "Homo sapiens",
                    "pdbCount": 5,
                    "pdbIds": [
                        "2UW2",
                        "3OLJ",
                        "3VPM",
                        "3VPN",
                        "3VPO"
                    ],
                    "associationScore": 0.5792,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "HBA1",
                    "name": "hemoglobin subunit alpha 1",
                    "uniprot": "P69905",
                    "organism": "Homo sapiens",
                    "pdbCount": 341,
                    "pdbIds": [
                        "1A00",
                        "1A01",
                        "1A0U",
                        "1A0Z",
                        "1A3N"
                    ],
                    "associationScore": 0.5767,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "HBA2",
                    "name": "hemoglobin subunit alpha 2",
                    "uniprot": "P69905",
                    "organism": "Homo sapiens",
                    "pdbCount": 341,
                    "pdbIds": [
                        "1A00",
                        "1A01",
                        "1A0U",
                        "1A0Z",
                        "1A3N"
                    ],
                    "associationScore": 0.5766,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "SELP",
                    "name": "selectin P",
                    "uniprot": "P16109",
                    "organism": "Homo sapiens",
                    "pdbCount": 5,
                    "pdbIds": [
                        "1FSB",
                        "1G1Q",
                        "1G1R",
                        "1G1S",
                        "1HES"
                    ],
                    "associationScore": 0.5582,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "BCL11A",
                    "name": "BCL11 transcription factor A",
                    "uniprot": "Q9H165",
                    "organism": "Homo sapiens",
                    "pdbCount": 12,
                    "pdbIds": [
                        "5VTB",
                        "6KI6",
                        "6U9Q",
                        "8DTN",
                        "8DTU"
                    ],
                    "associationScore": 0.4666,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                }
            ]
        },
        "major_depression": {
            "canonicalName": "major depressive disorder",
            "aliases": [
                "MDD",
                "depression",
                "major depression",
                "clinical depression",
                "unipolar depression"
            ],
            "efoId": "MONDO_0002009",
            "targetSource": "open_targets_api",
            "totalAssociatedTargets": 4960,
            "topTargets": [
                {
                    "geneSymbol": "DRD2",
                    "name": "dopamine receptor D2",
                    "uniprot": "P14416",
                    "organism": "Homo sapiens",
                    "pdbCount": 9,
                    "pdbIds": [
                        "5AER",
                        "6CM4",
                        "6LUQ",
                        "6VMS",
                        "7DFP"
                    ],
                    "associationScore": 0.7452,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "HTR2A",
                    "name": "5-hydroxytryptamine receptor 2A",
                    "uniprot": "P28223",
                    "organism": "Homo sapiens",
                    "pdbCount": 32,
                    "pdbIds": [
                        "6A93",
                        "6A94",
                        "6WGT",
                        "6WH4",
                        "6WHA"
                    ],
                    "associationScore": 0.7034,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "GABRA1",
                    "name": "gamma-aminobutyric acid type A receptor subunit alpha1",
                    "uniprot": "P14867",
                    "organism": "Homo sapiens",
                    "pdbCount": 78,
                    "pdbIds": [
                        "6CDU",
                        "6D1S",
                        "6D6T",
                        "6D6U",
                        "6HUG"
                    ],
                    "associationScore": 0.6825,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "GRIN2A",
                    "name": "glutamate ionotropic receptor NMDA type subunit 2A",
                    "uniprot": "Q12879",
                    "organism": "Homo sapiens",
                    "pdbCount": 35,
                    "pdbIds": [
                        "3NFL",
                        "5H8F",
                        "5H8H",
                        "5H8N",
                        "5H8Q"
                    ],
                    "associationScore": 0.6601,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "SLC6A4",
                    "name": "solute carrier family 6 member 4",
                    "uniprot": "P31645",
                    "organism": "Homo sapiens",
                    "pdbCount": 27,
                    "pdbIds": [
                        "5I6X",
                        "5I6Z",
                        "5I71",
                        "5I73",
                        "5I74"
                    ],
                    "associationScore": 0.641,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "HTR1A",
                    "name": "5-hydroxytryptamine receptor 1A",
                    "uniprot": "P08908",
                    "organism": "Homo sapiens",
                    "pdbCount": 18,
                    "pdbIds": [
                        "7E2X",
                        "7E2Y",
                        "7E2Z",
                        "8FY8",
                        "8FYE"
                    ],
                    "associationScore": 0.6367,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "SLC6A3",
                    "name": "solute carrier family 6 member 3",
                    "uniprot": "Q01959",
                    "organism": "Homo sapiens",
                    "pdbCount": 7,
                    "pdbIds": [
                        "8VBY",
                        "8Y2C",
                        "8Y2D",
                        "8Y2E",
                        "8Y2F"
                    ],
                    "associationScore": 0.6292,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                },
                {
                    "geneSymbol": "GABRG2",
                    "name": "gamma-aminobutyric acid type A receptor subunit gamma2",
                    "uniprot": "P18507",
                    "organism": "Homo sapiens",
                    "pdbCount": 60,
                    "pdbIds": [
                        "6D6T",
                        "6D6U",
                        "6HUG",
                        "6HUJ",
                        "6HUK"
                    ],
                    "associationScore": 0.6219,
                    "maturity": null,
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": false
                }
            ]
        },
        "als": {
            "canonicalName": "Amyotrophic lateral sclerosis",
            "aliases": ["ALS", "Lou Gehrig", "motor neuron disease", "amyotrophic lateral sclerosis"],
            "targetSource": "uniprot + known drug targets (needs SME review)",
            "topTargets": [
                {
                    "geneSymbol": "SOD1",
                    "name": "Superoxide dismutase [Cu-Zn]",
                    "uniprot": "P00441",
                    "organism": "Homo sapiens",
                    "pdbCount": 151,
                    "preferredDemoPdb": "1AZV",
                    "pdbIds": ["1AZV", "1BA9", "1DSW", "1FUN", "1HL4", "1HL5"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "FUS",
                    "name": "RNA-binding protein FUS",
                    "uniprot": "P35637",
                    "organism": "Homo sapiens",
                    "pdbCount": 23,
                    "preferredDemoPdb": "2LA6",
                    "pdbIds": ["2LA6", "2LCW", "4FDD", "4FQ3", "5W3N", "5XRR"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "TDP43",
                    "name": "TAR DNA-binding protein 43",
                    "uniprot": "Q13148",
                    "organism": "Homo sapiens",
                    "pdbCount": 44,
                    "preferredDemoPdb": "1WF0",
                    "pdbIds": ["1WF0", "2CQG", "2N2C", "2N3X", "2N4G", "2N4H"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                }
            ]
        },
        "atopic_dermatitis": {
            "canonicalName": "Atopic dermatitis",
            "aliases": ["eczema", "atopic dermatitis", "AD"],
            "targetSource": "uniprot + known drug targets (needs SME review)",
            "topTargets": [
                {
                    "geneSymbol": "JAK1",
                    "name": "Tyrosine-protein kinase JAK1",
                    "uniprot": "P23458",
                    "organism": "Homo sapiens",
                    "pdbCount": 51,
                    "preferredDemoPdb": "3EYG",
                    "pdbIds": ["3EYG", "3EYH", "4E4L", "4E4N", "4E5W", "4EHZ"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "JAK2",
                    "name": "Tyrosine-protein kinase JAK2",
                    "uniprot": "O60674",
                    "organism": "Homo sapiens",
                    "pdbCount": 162,
                    "preferredDemoPdb": "2B7A",
                    "pdbIds": ["2B7A", "2W1I", "2XA4", "3E62", "3E63", "3E64"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "PDE4",
                    "name": "cAMP-specific 3,5-cyclic phosphodiesterase 4B",
                    "uniprot": "Q07343",
                    "organism": "Homo sapiens",
                    "pdbCount": 42,
                    "preferredDemoPdb": "1F0J",
                    "pdbIds": ["1F0J", "1RO6", "1RO9", "1ROR", "1TB5", "1XLX"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "IL4R",
                    "name": "Interleukin-4 receptor subunit alpha",
                    "uniprot": "P24394",
                    "organism": "Homo sapiens",
                    "pdbCount": 10,
                    "preferredDemoPdb": "1IAR",
                    "pdbIds": ["1IAR", "1IRS", "3BPL", "3BPN", "3BPO", "5E4E"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "IL13",
                    "name": "Interleukin-13",
                    "uniprot": "P35225",
                    "organism": "Homo sapiens",
                    "pdbCount": 13,
                    "preferredDemoPdb": "1GA3",
                    "pdbIds": ["1GA3", "1IJZ", "1IK0", "3BPO", "3G6D", "3L5W"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                }
            ]
        },
        "cdiff": {
            "canonicalName": "Clostridioides difficile infection",
            "aliases": ["C. diff", "C. difficile", "CDI", "Clostridioides difficile"],
            "targetSource": "uniprot + known drug targets (needs SME review)",
            "topTargets": [
                {
                    "geneSymbol": "TcdB",
                    "name": "Toxin B",
                    "uniprot": "P18177",
                    "organism": "C. difficile",
                    "pdbCount": 32,
                    "preferredDemoPdb": "2BVL",
                    "pdbIds": ["2BVL", "2BVM", "4NC2", "4NP4", "5UQM", "5UQN"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "TcdA",
                    "name": "Toxin A",
                    "uniprot": "Q189K5",
                    "organism": "C. difficile",
                    "pdbCount": 0,
                    "pdbIds": [],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "alphafold",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "GyrB",
                    "name": "DNA gyrase subunit B",
                    "uniprot": "Q180U8",
                    "organism": "C. difficile",
                    "pdbCount": 1,
                    "preferredDemoPdb": "3UUW",
                    "pdbIds": ["3UUW"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "PPEP-1",
                    "name": "Metalloprotease",
                    "uniprot": "A0A1V0KAY6",
                    "organism": "C. difficile",
                    "pdbCount": 0,
                    "pdbIds": [],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "alphafold",
                    "reviewRequired": true
                }
            ]
        },
        "chagas": {
            "canonicalName": "Chagas disease",
            "aliases": ["Chagas", "Chagas disease", "T. cruzi", "Trypanosoma cruzi"],
            "targetSource": "uniprot + known drug targets (needs SME review)",
            "topTargets": [
                {
                    "geneSymbol": "CYP51",
                    "name": "Sterol 14-alpha demethylase",
                    "uniprot": "Q86FS5",
                    "organism": "T. cruzi",
                    "pdbCount": 0,
                    "pdbIds": [],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "alphafold",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "Cruzain",
                    "name": "Cruzipain (cysteine protease)",
                    "uniprot": "P25779",
                    "organism": "T. cruzi",
                    "pdbCount": 31,
                    "preferredDemoPdb": "1AIM",
                    "pdbIds": ["1AIM", "1EWL", "1EWM", "1EWO", "1EWP", "1F29"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "TcNTR",
                    "name": "Nitroreductase",
                    "uniprot": "Q4DPM0",
                    "organism": "T. cruzi",
                    "pdbCount": 0,
                    "pdbIds": [],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "alphafold",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "DHFR-TS",
                    "name": "Dihydrofolate reductase-thymidylate synthase",
                    "uniprot": "Q27793",
                    "organism": "T. cruzi",
                    "pdbCount": 8,
                    "preferredDemoPdb": "2H2Q",
                    "pdbIds": ["2H2Q", "3CL9", "3CLB", "3HBB", "3INV", "3IRM"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                }
            ]
        },
        "cystic_fibrosis": {
            "canonicalName": "Cystic fibrosis",
            "aliases": ["cystic fibrosis", "CF", "CFTR"],
            "targetSource": "uniprot + known drug targets (needs SME review)",
            "topTargets": [
                {
                    "geneSymbol": "CFTR",
                    "name": "Cystic fibrosis transmembrane conductance regulator",
                    "uniprot": "P13569",
                    "organism": "Homo sapiens",
                    "pdbCount": 58,
                    "preferredDemoPdb": "1XMI",
                    "pdbIds": ["1XMI", "1XMJ", "2BBO", "2BBS", "2BBT", "2LOB"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                }
            ]
        },
        "dengue": {
            "canonicalName": "Dengue fever",
            "aliases": ["dengue", "dengue virus", "DENV"],
            "targetSource": "uniprot + known drug targets (needs SME review)",
            "topTargets": [
                {
                    "geneSymbol": "NS3",
                    "name": "Serine protease/helicase NS3",
                    "uniprot": "P17763",
                    "organism": "Dengue virus 2",
                    "pdbCount": 14,
                    "preferredDemoPdb": "3J8D",
                    "pdbIds": ["3J8D", "3L6P", "3LKW", "4AL8", "4GSX", "4GT0"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "NS5",
                    "name": "RNA-directed RNA polymerase NS5",
                    "uniprot": "P17763",
                    "organism": "Dengue virus 2",
                    "pdbCount": 14,
                    "preferredDemoPdb": "3J8D",
                    "pdbIds": ["3J8D", "3L6P", "3LKW", "4AL8", "4GSX", "4GT0"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "E",
                    "name": "Envelope protein E",
                    "uniprot": "P29990",
                    "organism": "Dengue virus 2",
                    "pdbCount": 6,
                    "preferredDemoPdb": "2R69",
                    "pdbIds": ["2R69", "4O6B", "6WER", "6WY1", "6ZQU", "7K93"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "NS2B",
                    "name": "Non-structural protein NS2B",
                    "uniprot": "P14340",
                    "organism": "Dengue virus 2",
                    "pdbCount": 19,
                    "preferredDemoPdb": "3IYA",
                    "pdbIds": ["3IYA", "3J27", "3J2P", "4CBF", "4UIH", "6FLA"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "C",
                    "name": "Capsid protein C",
                    "uniprot": "P14340",
                    "organism": "Dengue virus 2",
                    "pdbCount": 19,
                    "preferredDemoPdb": "3IYA",
                    "pdbIds": ["3IYA", "3J27", "3J2P", "4CBF", "4UIH", "6FLA"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                }
            ]
        },
        "ebola": {
            "canonicalName": "Ebola virus disease",
            "aliases": ["ebola", "Ebola", "EVD", "ebola virus"],
            "targetSource": "uniprot + known drug targets (needs SME review)",
            "topTargets": [
                {
                    "geneSymbol": "GP",
                    "name": "Envelope glycoprotein",
                    "uniprot": "Q05320",
                    "organism": "Ebola virus",
                    "pdbCount": 52,
                    "preferredDemoPdb": "2EBO",
                    "pdbIds": ["2EBO", "2RLJ", "3CSY", "5FHC", "5HJ3", "5JQ3"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "VP35",
                    "name": "Polymerase cofactor VP35",
                    "uniprot": "Q05127",
                    "organism": "Ebola virus",
                    "pdbCount": 26,
                    "preferredDemoPdb": "3FKE",
                    "pdbIds": ["3FKE", "3L25", "3L26", "3L27", "3L28", "3L29"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "NP",
                    "name": "Nucleoprotein",
                    "uniprot": "P18272",
                    "organism": "Ebola virus",
                    "pdbCount": 16,
                    "preferredDemoPdb": "4QAZ",
                    "pdbIds": ["4QAZ", "4QB0", "4YPI", "4Z9P", "4ZTA", "4ZTG"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "VP40",
                    "name": "Matrix protein VP40",
                    "uniprot": "Q05128",
                    "organism": "Ebola virus",
                    "pdbCount": 12,
                    "preferredDemoPdb": "1H2C",
                    "pdbIds": ["1H2C", "1H2D", "2KQ0", "4EJE", "4LDB", "4LDD"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "L",
                    "name": "RNA-directed RNA polymerase L",
                    "uniprot": "Q05318",
                    "organism": "Ebola virus",
                    "pdbCount": 1,
                    "preferredDemoPdb": "9IP3",
                    "pdbIds": ["9IP3"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                }
            ]
        },
        "epilepsy": {
            "canonicalName": "Epilepsy",
            "aliases": ["epilepsy", "seizure disorder", "seizures"],
            "targetSource": "uniprot + known drug targets (needs SME review)",
            "topTargets": [
                {
                    "geneSymbol": "SV2A",
                    "name": "Synaptic vesicle glycoprotein 2A",
                    "uniprot": "Q7L0J3",
                    "organism": "Homo sapiens",
                    "pdbCount": 17,
                    "preferredDemoPdb": "4V11",
                    "pdbIds": ["4V11", "8JLC", "8JLE", "8JLF", "8JLG", "8JLH"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "GABRA1",
                    "name": "GABA-A receptor subunit alpha-1",
                    "uniprot": "P14867",
                    "organism": "Homo sapiens",
                    "pdbCount": 78,
                    "preferredDemoPdb": "6CDU",
                    "pdbIds": ["6CDU", "6D1S", "6D6T", "6D6U", "6HUG", "6HUJ"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "SCN1A",
                    "name": "Sodium channel protein type 1 subunit alpha",
                    "uniprot": "P35498",
                    "organism": "Homo sapiens",
                    "pdbCount": 1,
                    "preferredDemoPdb": "7DTD",
                    "pdbIds": ["7DTD"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "MTOR",
                    "name": "Serine/threonine-protein kinase mTOR",
                    "uniprot": "P42345",
                    "organism": "Homo sapiens",
                    "pdbCount": 54,
                    "preferredDemoPdb": "1AUE",
                    "pdbIds": ["1AUE", "1FAP", "1NSG", "2FAP", "2GAQ", "2NPU"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                }
            ]
        },
        "fungal_infection": {
            "canonicalName": "Invasive fungal infection",
            "aliases": ["fungal infection", "Aspergillus", "Candida", "antifungal"],
            "targetSource": "uniprot + known drug targets (needs SME review)",
            "topTargets": [
                {
                    "geneSymbol": "CYP51",
                    "name": "Lanosterol 14-alpha demethylase",
                    "uniprot": "P10613",
                    "organism": "C. albicans",
                    "pdbCount": 3,
                    "preferredDemoPdb": "5FSA",
                    "pdbIds": ["5FSA", "5TZ1", "5V5Z"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "GS",
                    "name": "1,3-beta-glucan synthase (FKS1)",
                    "uniprot": "P52911",
                    "organism": "C. albicans",
                    "pdbCount": 0,
                    "pdbIds": [],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "alphafold",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "DHODH",
                    "name": "Dihydroorotate dehydrogenase",
                    "uniprot": "Q0CEQ9",
                    "organism": "A. fumigatus",
                    "pdbCount": 0,
                    "pdbIds": [],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "alphafold",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "GWT1",
                    "name": "GPI-anchored wall transfer protein 1",
                    "uniprot": "Q5AF03",
                    "organism": "C. albicans",
                    "pdbCount": 1,
                    "preferredDemoPdb": "4LRU",
                    "pdbIds": ["4LRU"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                }
            ]
        },
        "gout": {
            "canonicalName": "Gout",
            "aliases": ["gout", "hyperuricemia", "uric acid"],
            "targetSource": "uniprot + known drug targets (needs SME review)",
            "topTargets": [
                {
                    "geneSymbol": "XO",
                    "name": "Xanthine dehydrogenase/oxidase",
                    "uniprot": "P47989",
                    "organism": "Homo sapiens",
                    "pdbCount": 2,
                    "preferredDemoPdb": "2CKJ",
                    "pdbIds": ["2CKJ", "2E1Q"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "URAT1",
                    "name": "Urate anion exchanger 1",
                    "uniprot": "Q96S37",
                    "organism": "Homo sapiens",
                    "pdbCount": 24,
                    "preferredDemoPdb": "8WJG",
                    "pdbIds": ["8WJG", "8WJQ", "9B1F", "9B1G", "9B1H", "9B1I"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                }
            ]
        },
        "hepatocellular_carcinoma": {
            "canonicalName": "Hepatocellular carcinoma",
            "aliases": ["liver cancer", "HCC", "hepatocellular carcinoma", "hepatoma"],
            "targetSource": "uniprot + known drug targets (needs SME review)",
            "topTargets": [
                {
                    "geneSymbol": "VEGFR2",
                    "name": "Vascular endothelial growth factor receptor 2",
                    "uniprot": "P35968",
                    "organism": "Homo sapiens",
                    "pdbCount": 54,
                    "preferredDemoPdb": "1VR2",
                    "pdbIds": ["1VR2", "1Y6A", "1Y6B", "1YWN", "2M59", "2MET"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "FGFR4",
                    "name": "Fibroblast growth factor receptor 4",
                    "uniprot": "P22455",
                    "organism": "Homo sapiens",
                    "pdbCount": 50,
                    "preferredDemoPdb": "4QQ5",
                    "pdbIds": ["4QQ5", "4QQC", "4QQJ", "4QQT", "4QRC", "4R6V"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "MET",
                    "name": "Hepatocyte growth factor receptor",
                    "uniprot": "P08581",
                    "organism": "Homo sapiens",
                    "pdbCount": 120,
                    "preferredDemoPdb": "1FYR",
                    "pdbIds": ["1FYR", "1R0P", "1R1W", "1SHY", "1SSL", "2G15"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "CTNNB1",
                    "name": "Catenin beta-1",
                    "uniprot": "P35222",
                    "organism": "Homo sapiens",
                    "pdbCount": 47,
                    "preferredDemoPdb": "1G3J",
                    "pdbIds": ["1G3J", "1JDH", "1JPW", "1LUJ", "1P22", "1QZ7"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "TP53",
                    "name": "Cellular tumor antigen p53",
                    "uniprot": "P04637",
                    "organism": "Homo sapiens",
                    "pdbCount": 286,
                    "preferredDemoPdb": "1A1U",
                    "pdbIds": ["1A1U", "1AIE", "1C26", "1DT7", "1GZH", "1H26"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                }
            ]
        },
        "huntington": {
            "canonicalName": "Huntington's disease",
            "aliases": ["Huntington", "Huntington's", "HD", "huntingtin"],
            "targetSource": "uniprot + known drug targets (needs SME review)",
            "topTargets": [
                {
                    "geneSymbol": "HTT",
                    "name": "Huntingtin",
                    "uniprot": "P42858",
                    "organism": "Homo sapiens",
                    "pdbCount": 30,
                    "preferredDemoPdb": "2LD0",
                    "pdbIds": ["2LD0", "2LD2", "3IO4", "3IO6", "3IOR", "3IOT"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "HDAC4",
                    "name": "Histone deacetylase 4",
                    "uniprot": "P56524",
                    "organism": "Homo sapiens",
                    "pdbCount": 19,
                    "preferredDemoPdb": "2H8N",
                    "pdbIds": ["2H8N", "2O94", "2VQJ", "2VQM", "2VQO", "2VQQ"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "MTOR",
                    "name": "Serine/threonine-protein kinase mTOR",
                    "uniprot": "P42345",
                    "organism": "Homo sapiens",
                    "pdbCount": 54,
                    "preferredDemoPdb": "1AUE",
                    "pdbIds": ["1AUE", "1FAP", "1NSG", "2FAP", "2GAQ", "2NPU"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                }
            ]
        },
        "ibd": {
            "canonicalName": "Inflammatory bowel disease",
            "aliases": ["IBD", "inflammatory bowel disease"],
            "targetSource": "uniprot + known drug targets (needs SME review)",
            "topTargets": [
                {
                    "geneSymbol": "TNF",
                    "name": "Tumor necrosis factor",
                    "uniprot": "P01375",
                    "organism": "Homo sapiens",
                    "pdbCount": 49,
                    "preferredDemoPdb": "1A8M",
                    "pdbIds": ["1A8M", "1TNF", "2AZ5", "2E7A", "2TUN", "2ZJC"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "JAK1",
                    "name": "Tyrosine-protein kinase JAK1",
                    "uniprot": "P23458",
                    "organism": "Homo sapiens",
                    "pdbCount": 51,
                    "preferredDemoPdb": "3EYG",
                    "pdbIds": ["3EYG", "3EYH", "4E4L", "4E4N", "4E5W", "4EHZ"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "IL23A",
                    "name": "Interleukin-23 subunit alpha",
                    "uniprot": "Q9NPF7",
                    "organism": "Homo sapiens",
                    "pdbCount": 15,
                    "preferredDemoPdb": "3D85",
                    "pdbIds": ["3D85", "3D87", "3DUH", "3QWR", "4GRW", "5MJ3"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "SMAD7",
                    "name": "Mothers against decapentaplegic homolog 7",
                    "uniprot": "O15105",
                    "organism": "Homo sapiens",
                    "pdbCount": 7,
                    "preferredDemoPdb": "2DJY",
                    "pdbIds": ["2DJY", "2KXQ", "2LTV", "2LTW", "2LTX", "2LTY"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "S1PR1",
                    "name": "Sphingosine 1-phosphate receptor 1",
                    "uniprot": "P21453",
                    "organism": "Homo sapiens",
                    "pdbCount": 17,
                    "preferredDemoPdb": "3V2W",
                    "pdbIds": ["3V2W", "3V2Y", "7EO2", "7EO4", "7EVY", "7EVZ"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                }
            ]
        },
        "influenza": {
            "canonicalName": "Influenza",
            "aliases": ["flu", "influenza", "influenza A", "influenza virus"],
            "targetSource": "uniprot + known drug targets (needs SME review)",
            "topTargets": [
                {
                    "geneSymbol": "NA",
                    "name": "Neuraminidase",
                    "uniprot": "P03472",
                    "organism": "Influenza A",
                    "pdbCount": 52,
                    "preferredDemoPdb": "1A14",
                    "pdbIds": ["1A14", "1BJI", "1F8B", "1F8C", "1F8D", "1F8E"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "M2",
                    "name": "Matrix protein 2 (proton channel)",
                    "uniprot": "P06821",
                    "organism": "Influenza A",
                    "pdbCount": 1,
                    "preferredDemoPdb": "5DLM",
                    "pdbIds": ["5DLM"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "PA",
                    "name": "Polymerase acidic protein",
                    "uniprot": "P31343",
                    "organism": "Influenza A",
                    "pdbCount": 1,
                    "preferredDemoPdb": "2W69",
                    "pdbIds": ["2W69"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "HA",
                    "name": "Hemagglutinin",
                    "uniprot": "P03437",
                    "organism": "Influenza A",
                    "pdbCount": 42,
                    "preferredDemoPdb": "1EO8",
                    "pdbIds": ["1EO8", "1FYT", "1HA0", "1HGG", "1HTM", "1J8H"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "NP",
                    "name": "Nucleoprotein",
                    "uniprot": "P03466",
                    "organism": "Influenza A",
                    "pdbCount": 8,
                    "preferredDemoPdb": "2BST",
                    "pdbIds": ["2BST", "2WFS", "4NQV", "4ZDU", "5NPZ", "5NQ3"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                }
            ]
        },
        "leishmaniasis": {
            "canonicalName": "Leishmaniasis",
            "aliases": ["leishmaniasis", "Leishmania", "kala-azar"],
            "targetSource": "uniprot + known drug targets (needs SME review)",
            "topTargets": [
                {
                    "geneSymbol": "PTR1",
                    "name": "Pteridine reductase 1",
                    "uniprot": "Q01782",
                    "organism": "L. major",
                    "pdbCount": 14,
                    "preferredDemoPdb": "1E7W",
                    "pdbIds": ["1E7W", "1E92", "1W0C", "2BF7", "2BFA", "2BFM"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "CYP51",
                    "name": "Sterol 14-alpha demethylase",
                    "uniprot": "E9AHZ1",
                    "organism": "L. infantum",
                    "pdbCount": 0,
                    "pdbIds": [],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "alphafold",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "DHFR-TS",
                    "name": "Dihydrofolate reductase-thymidylate synthase",
                    "uniprot": "P07382",
                    "organism": "L. major",
                    "pdbCount": 0,
                    "pdbIds": [],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "alphafold",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "TopIB",
                    "name": "DNA topoisomerase 1",
                    "uniprot": "Q4Q259",
                    "organism": "L. donovani",
                    "pdbCount": 0,
                    "pdbIds": [],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "alphafold",
                    "reviewRequired": true
                }
            ]
        },
        "leukemia_aml": {
            "canonicalName": "Acute myeloid leukemia",
            "aliases": ["AML", "acute myeloid leukemia", "myeloid leukemia"],
            "targetSource": "uniprot + known drug targets (needs SME review)",
            "topTargets": [
                {
                    "geneSymbol": "FLT3",
                    "name": "Receptor-type tyrosine-protein kinase FLT3",
                    "uniprot": "P36888",
                    "organism": "Homo sapiens",
                    "pdbCount": 11,
                    "preferredDemoPdb": "1RJB",
                    "pdbIds": ["1RJB", "3QS7", "3QS9", "4RT7", "4XUF", "5X02"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "IDH2",
                    "name": "Isocitrate dehydrogenase [NADP] mitochondrial",
                    "uniprot": "P48735",
                    "organism": "Homo sapiens",
                    "pdbCount": 11,
                    "preferredDemoPdb": "4JA8",
                    "pdbIds": ["4JA8", "5GIS", "5I95", "5I96", "5SVN", "5SVO"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "IDH1",
                    "name": "Isocitrate dehydrogenase [NADP] cytoplasmic",
                    "uniprot": "O75874",
                    "organism": "Homo sapiens",
                    "pdbCount": 58,
                    "preferredDemoPdb": "1T09",
                    "pdbIds": ["1T09", "1T0L", "3INM", "3MAP", "3MAR", "3MAS"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "BCL2",
                    "name": "Apoptosis regulator Bcl-2",
                    "uniprot": "P10415",
                    "organism": "Homo sapiens",
                    "pdbCount": 46,
                    "preferredDemoPdb": "1G5M",
                    "pdbIds": ["1G5M", "1GJH", "1YSW", "2O21", "2O22", "2O2F"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "DOT1L",
                    "name": "Histone-lysine N-methyltransferase DOT1L",
                    "uniprot": "Q8TEK3",
                    "organism": "Homo sapiens",
                    "pdbCount": 48,
                    "preferredDemoPdb": "1NW3",
                    "pdbIds": ["1NW3", "2MV7", "3QOW", "3QOX", "3SR4", "3SX0"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                }
            ]
        },
        "lymphoma": {
            "canonicalName": "Non-Hodgkin lymphoma",
            "aliases": ["lymphoma", "NHL", "non-Hodgkin's lymphoma", "B-cell lymphoma"],
            "targetSource": "uniprot + known drug targets (needs SME review)",
            "topTargets": [
                {
                    "geneSymbol": "BTK",
                    "name": "Tyrosine-protein kinase BTK (Bruton's)",
                    "uniprot": "Q06187",
                    "organism": "Homo sapiens",
                    "pdbCount": 133,
                    "preferredDemoPdb": "1AWW",
                    "pdbIds": ["1AWW", "1AWX", "1B55", "1BTK", "1BWN", "1K2P"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "BCL2",
                    "name": "Apoptosis regulator Bcl-2",
                    "uniprot": "P10415",
                    "organism": "Homo sapiens",
                    "pdbCount": 46,
                    "preferredDemoPdb": "1G5M",
                    "pdbIds": ["1G5M", "1GJH", "1YSW", "2O21", "2O22", "2O2F"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "PI3Kd",
                    "name": "PI3-kinase p110 subunit delta",
                    "uniprot": "O00329",
                    "organism": "Homo sapiens",
                    "pdbCount": 18,
                    "preferredDemoPdb": "5DXU",
                    "pdbIds": ["5DXU", "5M6U", "5T8F", "5UBT", "5VLR", "6G6W"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "EZH2",
                    "name": "Histone-lysine N-methyltransferase EZH2",
                    "uniprot": "Q15910",
                    "organism": "Homo sapiens",
                    "pdbCount": 38,
                    "preferredDemoPdb": "4MI0",
                    "pdbIds": ["4MI0", "4MI5", "5GSA", "5H14", "5H15", "5H17"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                }
            ]
        },
        "malaria": {
            "canonicalName": "Malaria",
            "aliases": ["malaria", "P. falciparum", "Plasmodium"],
            "targetSource": "uniprot + known drug targets (needs SME review)",
            "topTargets": [
                {
                    "geneSymbol": "PfDHFR",
                    "name": "Dihydrofolate reductase-thymidylate synthase",
                    "uniprot": "P13922",
                    "organism": "P. falciparum",
                    "pdbCount": 9,
                    "preferredDemoPdb": "1J3I",
                    "pdbIds": ["1J3I", "1J3J", "1J3K", "3DG8", "3DGA", "7CTW"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "PfDHODH",
                    "name": "Dihydroorotate dehydrogenase",
                    "uniprot": "Q08210",
                    "organism": "P. falciparum",
                    "pdbCount": 37,
                    "preferredDemoPdb": "1TV5",
                    "pdbIds": ["1TV5", "3I65", "3I68", "3I6R", "3O8A", "3SFK"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "PfATP4",
                    "name": "Cation-transporting ATPase 4",
                    "uniprot": "Q8I2R4",
                    "organism": "P. falciparum",
                    "pdbCount": 0,
                    "pdbIds": [],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "alphafold",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "PfKRS",
                    "name": "Lysyl-tRNA synthetase",
                    "uniprot": "Q6LFN6",
                    "organism": "P. falciparum",
                    "pdbCount": 0,
                    "pdbIds": [],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "alphafold",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "PfPI4K",
                    "name": "Phosphatidylinositol 4-kinase",
                    "uniprot": "Q8ILT9",
                    "organism": "P. falciparum",
                    "pdbCount": 0,
                    "pdbIds": [],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "alphafold",
                    "reviewRequired": true
                }
            ]
        },
        "melanoma": {
            "canonicalName": "Melanoma",
            "aliases": ["melanoma", "skin cancer", "malignant melanoma"],
            "targetSource": "uniprot + known drug targets (needs SME review)",
            "topTargets": [
                {
                    "geneSymbol": "BRAF",
                    "name": "Serine/threonine-protein kinase B-raf",
                    "uniprot": "P15056",
                    "organism": "Homo sapiens",
                    "pdbCount": 127,
                    "preferredDemoPdb": "1UWH",
                    "pdbIds": ["1UWH", "1UWJ", "2FB8", "2L05", "3C4C", "3D4Q"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "MEK1",
                    "name": "Dual specificity mitogen-activated protein kinase kinase 1",
                    "uniprot": "Q02750",
                    "organism": "Homo sapiens",
                    "pdbCount": 84,
                    "preferredDemoPdb": "1S9J",
                    "pdbIds": ["1S9J", "2P55", "3DV3", "3DY7", "3E8N", "3EQB"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "CDK4",
                    "name": "Cyclin-dependent kinase 4",
                    "uniprot": "P11802",
                    "organism": "Homo sapiens",
                    "pdbCount": 15,
                    "preferredDemoPdb": "2W96",
                    "pdbIds": ["2W96", "2W99", "2W9F", "2W9Z", "3G33", "5FWK"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "PD1",
                    "name": "Programmed cell death protein 1",
                    "uniprot": "Q15116",
                    "organism": "Homo sapiens",
                    "pdbCount": 36,
                    "preferredDemoPdb": "2M2D",
                    "pdbIds": ["2M2D", "3RRQ", "4ZQK", "5B8C", "5GGR", "5GGS"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                }
            ]
        },
        "migraine": {
            "canonicalName": "Migraine",
            "aliases": ["migraine", "migraines", "headache disorder"],
            "targetSource": "uniprot + known drug targets (needs SME review)",
            "topTargets": [
                {
                    "geneSymbol": "CGRPR",
                    "name": "Calcitonin gene-related peptide type 1 receptor",
                    "uniprot": "Q16602",
                    "organism": "Homo sapiens",
                    "pdbCount": 23,
                    "preferredDemoPdb": "3AQF",
                    "pdbIds": ["3AQF", "3N7P", "3N7R", "3N7S", "4RWF", "4RWG"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "HTR1F",
                    "name": "5-hydroxytryptamine receptor 1F",
                    "uniprot": "P30428",
                    "organism": "Homo sapiens",
                    "pdbCount": 0,
                    "pdbIds": [],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "alphafold",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "TRPV1",
                    "name": "Transient receptor potential cation channel V1",
                    "uniprot": "Q8NER1",
                    "organism": "Homo sapiens",
                    "pdbCount": 8,
                    "preferredDemoPdb": "6L93",
                    "pdbIds": ["6L93", "8GF8", "8GF9", "8GFA", "8JQR", "8X94"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                }
            ]
        },
        "mpox": {
            "canonicalName": "Mpox (monkeypox)",
            "aliases": ["mpox", "monkeypox", "MPXV"],
            "targetSource": "uniprot + known drug targets (needs SME review)",
            "topTargets": [
                {
                    "geneSymbol": "F13L",
                    "name": "Palmitylated EEV membrane protein",
                    "uniprot": "Q8V4Y0",
                    "organism": "Vaccinia virus",
                    "pdbCount": 0,
                    "pdbIds": [],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "alphafold",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "E9L",
                    "name": "DNA polymerase",
                    "uniprot": "P06855",
                    "organism": "Vaccinia virus",
                    "pdbCount": 7,
                    "preferredDemoPdb": "1LTQ",
                    "pdbIds": ["1LTQ", "1LY1", "1RC8", "1RPZ", "1RRC", "2IA5"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "D4R",
                    "name": "Uracil-DNA glycosylase",
                    "uniprot": "P20536",
                    "organism": "Vaccinia virus",
                    "pdbCount": 9,
                    "preferredDemoPdb": "4OD8",
                    "pdbIds": ["4OD8", "4ODA", "4YGM", "4YIG", "5JKR", "5JKS"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "I7L",
                    "name": "Core protein proteinase",
                    "uniprot": "A0A2I1AL67",
                    "organism": "Monkeypox virus",
                    "pdbCount": 0,
                    "pdbIds": [],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "alphafold",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "A48R",
                    "name": "Thymidylate kinase",
                    "uniprot": "Q8V4R7",
                    "organism": "Vaccinia virus",
                    "pdbCount": 0,
                    "pdbIds": [],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "alphafold",
                    "reviewRequired": true
                }
            ]
        },
        "mrsa": {
            "canonicalName": "MRSA infection",
            "aliases": ["MRSA", "Staphylococcus aureus", "staph infection", "staph aureus"],
            "targetSource": "uniprot + known drug targets (needs SME review)",
            "topTargets": [
                {
                    "geneSymbol": "PBP2a",
                    "name": "Penicillin-binding protein 2a",
                    "uniprot": "P07944",
                    "organism": "S. aureus",
                    "pdbCount": 0,
                    "pdbIds": [],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "alphafold",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "FtsZ",
                    "name": "Cell division protein FtsZ",
                    "uniprot": "P17865",
                    "organism": "S. aureus",
                    "pdbCount": 7,
                    "preferredDemoPdb": "2RHH",
                    "pdbIds": ["2RHH", "2RHJ", "2RHL", "2RHO", "2VAM", "2VXY"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "MurB",
                    "name": "UDP-N-acetylenolpyruvoylglucosamine reductase",
                    "uniprot": "Q99SZ7",
                    "organism": "S. aureus",
                    "pdbCount": 0,
                    "pdbIds": [],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "alphafold",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "GyrA",
                    "name": "DNA gyrase subunit A",
                    "uniprot": "P0C4H2",
                    "organism": "S. aureus",
                    "pdbCount": 0,
                    "pdbIds": [],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "alphafold",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "FabI",
                    "name": "Enoyl-[acyl-carrier-protein] reductase",
                    "uniprot": "P0A025",
                    "organism": "S. aureus",
                    "pdbCount": 0,
                    "pdbIds": [],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "alphafold",
                    "reviewRequired": true
                }
            ]
        },
        "multiple_myeloma": {
            "canonicalName": "Multiple myeloma",
            "aliases": ["myeloma", "multiple myeloma", "MM", "plasma cell myeloma"],
            "targetSource": "uniprot + known drug targets (needs SME review)",
            "topTargets": [
                {
                    "geneSymbol": "PSMC5",
                    "name": "Proteasome 26S subunit (ATPase 5)",
                    "uniprot": "P17980",
                    "organism": "Homo sapiens",
                    "pdbCount": 78,
                    "preferredDemoPdb": "5GJQ",
                    "pdbIds": ["5GJQ", "5GJR", "5L4G", "5LN3", "5M32", "5T0C"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "CRBN",
                    "name": "Protein cereblon",
                    "uniprot": "Q96SW2",
                    "organism": "Homo sapiens",
                    "pdbCount": 55,
                    "preferredDemoPdb": "4M91",
                    "pdbIds": ["4M91", "4TZ4", "5FQD", "5HXB", "5V3O", "6BN7"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "HDAC6",
                    "name": "Histone deacetylase 6",
                    "uniprot": "Q9UBN7",
                    "organism": "Homo sapiens",
                    "pdbCount": 21,
                    "preferredDemoPdb": "3C5K",
                    "pdbIds": ["3C5K", "3GV4", "3PHD", "5B8D", "5EDU", "5KH3"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "BCL2",
                    "name": "Apoptosis regulator Bcl-2",
                    "uniprot": "P10415",
                    "organism": "Homo sapiens",
                    "pdbCount": 46,
                    "preferredDemoPdb": "1G5M",
                    "pdbIds": ["1G5M", "1GJH", "1YSW", "2O21", "2O22", "2O2F"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "XPO1",
                    "name": "Exportin-1",
                    "uniprot": "O14980",
                    "organism": "Homo sapiens",
                    "pdbCount": 10,
                    "preferredDemoPdb": "1W9C",
                    "pdbIds": ["1W9C", "2L1L", "3GB8", "4BSM", "4BSN", "5DIS"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                }
            ]
        },
        "nash": {
            "canonicalName": "Non-alcoholic steatohepatitis",
            "aliases": ["NASH", "MASH", "fatty liver disease", "NAFLD", "steatohepatitis"],
            "targetSource": "uniprot + known drug targets (needs SME review)",
            "topTargets": [
                {
                    "geneSymbol": "THRb",
                    "name": "Thyroid hormone receptor beta",
                    "uniprot": "P10828",
                    "organism": "Homo sapiens",
                    "pdbCount": 34,
                    "preferredDemoPdb": "1BSX",
                    "pdbIds": ["1BSX", "1N46", "1NAX", "1NQ0", "1NQ1", "1NQ2"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "FXR",
                    "name": "Bile acid receptor (NR1H4)",
                    "uniprot": "Q96RI1",
                    "organism": "Homo sapiens",
                    "pdbCount": 89,
                    "preferredDemoPdb": "1OSH",
                    "pdbIds": ["1OSH", "3BEJ", "3DCT", "3DCU", "3FLI", "3FXV"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "ACC1",
                    "name": "Acetyl-CoA carboxylase 1",
                    "uniprot": "Q13085",
                    "organism": "Homo sapiens",
                    "pdbCount": 10,
                    "preferredDemoPdb": "2YL2",
                    "pdbIds": ["2YL2", "3COJ", "4ASI", "6G2D", "6G2H", "6G2I"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "ASK1",
                    "name": "Mitogen-activated protein kinase kinase kinase 5",
                    "uniprot": "Q99683",
                    "organism": "Homo sapiens",
                    "pdbCount": 27,
                    "preferredDemoPdb": "2CLQ",
                    "pdbIds": ["2CLQ", "3VW6", "4BF2", "4BHN", "4BIB", "4BIC"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "GLP1R",
                    "name": "Glucagon-like peptide 1 receptor",
                    "uniprot": "P43220",
                    "organism": "Homo sapiens",
                    "pdbCount": 56,
                    "preferredDemoPdb": "3C59",
                    "pdbIds": ["3C59", "3C5T", "3IOL", "4ZGM", "5E94", "5NX2"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                }
            ]
        },
        "osteoporosis": {
            "canonicalName": "Osteoporosis",
            "aliases": ["osteoporosis", "bone loss", "bone density"],
            "targetSource": "uniprot + known drug targets (needs SME review)",
            "topTargets": [
                {
                    "geneSymbol": "CTSK",
                    "name": "Cathepsin K",
                    "uniprot": "P43235",
                    "organism": "Homo sapiens",
                    "pdbCount": 70,
                    "preferredDemoPdb": "1ATK",
                    "pdbIds": ["1ATK", "1AU0", "1AU2", "1AU3", "1AU4", "1AYU"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "RANKL",
                    "name": "Tumor necrosis factor ligand superfamily member 11",
                    "uniprot": "O14788",
                    "organism": "Homo sapiens",
                    "pdbCount": 2,
                    "preferredDemoPdb": "3URF",
                    "pdbIds": ["3URF", "5BNQ"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "SOST",
                    "name": "Sclerostin",
                    "uniprot": "Q9BQB4",
                    "organism": "Homo sapiens",
                    "pdbCount": 3,
                    "preferredDemoPdb": "2K8P",
                    "pdbIds": ["2K8P", "3SOV", "6L6R"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "PTH1R",
                    "name": "Parathyroid hormone/PTHrP receptor",
                    "uniprot": "Q03431",
                    "organism": "Homo sapiens",
                    "pdbCount": 36,
                    "preferredDemoPdb": "1BL1",
                    "pdbIds": ["1BL1", "3C4M", "3H3G", "3L2J", "4Z8J", "5EMB"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                }
            ]
        },
        "pain": {
            "canonicalName": "Chronic pain",
            "aliases": ["chronic pain", "pain", "neuropathic pain", "pain management"],
            "targetSource": "uniprot + known drug targets (needs SME review)",
            "topTargets": [
                {
                    "geneSymbol": "COX2",
                    "name": "Prostaglandin G/H synthase 2",
                    "uniprot": "P35354",
                    "organism": "Homo sapiens",
                    "pdbCount": 7,
                    "preferredDemoPdb": "5F19",
                    "pdbIds": ["5F19", "5F1A", "5IKQ", "5IKR", "5IKT", "5IKV"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "Nav1.7",
                    "name": "Sodium channel protein type 9 subunit alpha",
                    "uniprot": "Q15858",
                    "organism": "Homo sapiens",
                    "pdbCount": 41,
                    "preferredDemoPdb": "5EK0",
                    "pdbIds": ["5EK0", "6J8G", "6J8H", "6J8I", "6J8J", "6N4Q"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "TRPV1",
                    "name": "Transient receptor potential cation channel V1",
                    "uniprot": "Q8NER1",
                    "organism": "Homo sapiens",
                    "pdbCount": 8,
                    "preferredDemoPdb": "6L93",
                    "pdbIds": ["6L93", "8GF8", "8GF9", "8GFA", "8JQR", "8X94"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "MOR",
                    "name": "Mu-type opioid receptor",
                    "uniprot": "P35372",
                    "organism": "Homo sapiens",
                    "pdbCount": 15,
                    "preferredDemoPdb": "8EF5",
                    "pdbIds": ["8EF5", "8EF6", "8EFB", "8EFL", "8EFO", "8EFQ"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "FAAH",
                    "name": "Fatty-acid amide hydrolase",
                    "uniprot": "O00519",
                    "organism": "Homo sapiens",
                    "pdbCount": 0,
                    "pdbIds": [],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "alphafold",
                    "reviewRequired": true
                }
            ]
        },
        "pancreatic_cancer": {
            "canonicalName": "Pancreatic cancer",
            "aliases": ["pancreatic cancer", "PDAC", "pancreatic adenocarcinoma"],
            "targetSource": "uniprot + known drug targets (needs SME review)",
            "topTargets": [
                {
                    "geneSymbol": "KRAS",
                    "name": "GTPase KRas",
                    "uniprot": "P01116",
                    "organism": "Homo sapiens",
                    "pdbCount": 402,
                    "preferredDemoPdb": "1D8D",
                    "pdbIds": ["1D8D", "1D8E", "1KZO", "1KZP", "1N4P", "1N4Q"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "EGFR",
                    "name": "Epidermal growth factor receptor",
                    "uniprot": "P00533",
                    "organism": "Homo sapiens",
                    "pdbCount": 351,
                    "preferredDemoPdb": "1IVO",
                    "pdbIds": ["1IVO", "1M14", "1M17", "1MOX", "1NQL", "1XKK"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "CDK4",
                    "name": "Cyclin-dependent kinase 4",
                    "uniprot": "P11802",
                    "organism": "Homo sapiens",
                    "pdbCount": 15,
                    "preferredDemoPdb": "2W96",
                    "pdbIds": ["2W96", "2W99", "2W9F", "2W9Z", "3G33", "5FWK"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "PARP1",
                    "name": "Poly [ADP-ribose] polymerase 1",
                    "uniprot": "P09874",
                    "organism": "Homo sapiens",
                    "pdbCount": 102,
                    "preferredDemoPdb": "1UK0",
                    "pdbIds": ["1UK0", "1UK1", "1WOK", "2COK", "2CR9", "2CS2"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                }
            ]
        },
        "prostate_cancer": {
            "canonicalName": "Prostate cancer",
            "aliases": ["prostate cancer", "prostate carcinoma", "PCa"],
            "targetSource": "uniprot + known drug targets (needs SME review)",
            "topTargets": [
                {
                    "geneSymbol": "AR",
                    "name": "Androgen receptor",
                    "uniprot": "P10275",
                    "organism": "Homo sapiens",
                    "pdbCount": 94,
                    "preferredDemoPdb": "1E3G",
                    "pdbIds": ["1E3G", "1GS4", "1T5Z", "1T63", "1T65", "1XJ7"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "CYP17A1",
                    "name": "Steroid 17-alpha-hydroxylase/17,20-lyase",
                    "uniprot": "P05093",
                    "organism": "Homo sapiens",
                    "pdbCount": 17,
                    "preferredDemoPdb": "3RUK",
                    "pdbIds": ["3RUK", "3SWZ", "4NKV", "4NKW", "4NKX", "4NKY"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "PARP1",
                    "name": "Poly [ADP-ribose] polymerase 1",
                    "uniprot": "P09874",
                    "organism": "Homo sapiens",
                    "pdbCount": 102,
                    "preferredDemoPdb": "1UK0",
                    "pdbIds": ["1UK0", "1UK1", "1WOK", "2COK", "2CR9", "2CS2"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "AKT1",
                    "name": "RAC-alpha serine/threonine-protein kinase",
                    "uniprot": "P31749",
                    "organism": "Homo sapiens",
                    "pdbCount": 42,
                    "preferredDemoPdb": "1H10",
                    "pdbIds": ["1H10", "1UNP", "1UNQ", "1UNR", "2UVM", "2UZR"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "PIK3CA",
                    "name": "PI3-kinase p110 subunit alpha",
                    "uniprot": "P42336",
                    "organism": "Homo sapiens",
                    "pdbCount": 117,
                    "preferredDemoPdb": "2ENQ",
                    "pdbIds": ["2ENQ", "2RD0", "3HHM", "3HIZ", "3ZIM", "4JPS"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                }
            ]
        },
        "rsv": {
            "canonicalName": "Respiratory syncytial virus infection",
            "aliases": ["RSV", "respiratory syncytial virus"],
            "targetSource": "uniprot + known drug targets (needs SME review)",
            "topTargets": [
                {
                    "geneSymbol": "F",
                    "name": "Fusion glycoprotein F0",
                    "uniprot": "P03420",
                    "organism": "RSV",
                    "pdbCount": 79,
                    "preferredDemoPdb": "2MDP",
                    "pdbIds": ["2MDP", "3IXT", "3KPE", "3O41", "3O45", "3RKI"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "N",
                    "name": "Nucleoprotein",
                    "uniprot": "P03418",
                    "organism": "RSV",
                    "pdbCount": 16,
                    "preferredDemoPdb": "2WJ8",
                    "pdbIds": ["2WJ8", "2YHM", "4BKK", "4UC6", "4UC7", "4UC8"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "L",
                    "name": "RNA-directed RNA polymerase L",
                    "uniprot": "P28887",
                    "organism": "RSV",
                    "pdbCount": 8,
                    "preferredDemoPdb": "6PZK",
                    "pdbIds": ["6PZK", "6UEN", "8FPI", "8FU3", "8SNX", "8SNY"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "P",
                    "name": "Phosphoprotein",
                    "uniprot": "P03421",
                    "organism": "RSV",
                    "pdbCount": 11,
                    "preferredDemoPdb": "4UCB",
                    "pdbIds": ["4UCB", "6G0Y", "6PZK", "6UEN", "6YP5", "8FPI"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "G",
                    "name": "Major surface glycoprotein G",
                    "uniprot": "P03423",
                    "organism": "RSV",
                    "pdbCount": 8,
                    "preferredDemoPdb": "5WN9",
                    "pdbIds": ["5WN9", "5WNA", "5WNB", "6UVO", "7T8W", "9CQA"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                }
            ]
        },
        "zika": {
            "canonicalName": "Zika virus disease",
            "aliases": ["zika", "Zika", "ZIKV", "zika virus"],
            "targetSource": "uniprot + known drug targets (needs SME review)",
            "topTargets": [
                {
                    "geneSymbol": "NS3",
                    "name": "Serine protease NS3",
                    "uniprot": "Q32ZE1",
                    "organism": "Zika virus",
                    "pdbCount": 273,
                    "preferredDemoPdb": "5GJ4",
                    "pdbIds": ["5GJ4", "5GPI", "5GXJ", "5JPS", "5JRZ", "5K6K"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "NS5",
                    "name": "RNA-directed RNA polymerase NS5",
                    "uniprot": "Q32ZE1",
                    "organism": "Zika virus",
                    "pdbCount": 273,
                    "preferredDemoPdb": "5GJ4",
                    "pdbIds": ["5GJ4", "5GPI", "5GXJ", "5JPS", "5JRZ", "5K6K"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "E",
                    "name": "Envelope protein E",
                    "uniprot": "Q32ZE1",
                    "organism": "Zika virus",
                    "pdbCount": 273,
                    "preferredDemoPdb": "5GJ4",
                    "pdbIds": ["5GJ4", "5GPI", "5GXJ", "5JPS", "5JRZ", "5K6K"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "NS2B",
                    "name": "Serine protease subunit NS2B",
                    "uniprot": "Q32ZE1",
                    "organism": "Zika virus",
                    "pdbCount": 273,
                    "preferredDemoPdb": "5GJ4",
                    "pdbIds": ["5GJ4", "5GPI", "5GXJ", "5JPS", "5JRZ", "5K6K"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                },
                {
                    "geneSymbol": "C",
                    "name": "Capsid protein C",
                    "uniprot": "Q32ZE1",
                    "organism": "Zika virus",
                    "pdbCount": 273,
                    "preferredDemoPdb": "5GJ4",
                    "pdbIds": ["5GJ4", "5GPI", "5GXJ", "5JPS", "5JRZ", "5K6K"],
                    "maturity": "unreviewed",
                    "maturityNote": null,
                    "structureSource": "pdb",
                    "reviewRequired": true
                }
            ]
        }
    }
};

export default diseaseTargets;

